import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment as env } from '../../../environments/environment';
import { ScannersService } from '../../@services/scanners.service';
import { AuthService } from '../../@services/auth.service';
import { ToastService } from '../../@services/toast.service';
import { ClaimsEditorComponent } from '../shared/claims-editor/claims-editor.component';

/**
 * Admin SSO Tester — exercise the iframe / sid handoff API end-to-end
 * from the admin browser. Useful for:
 *   - Verifying a scanner's mint endpoint returns a sane url
 *   - Confirming the per-product SPA accepts the sid (load in iframe)
 *   - Debugging "Session expired" / redirect-loop reports
 *   - Showing a customer's onboarding engineer what the API contract is
 *
 * What it does:
 *   1. Pick a scanner from F2-ADMIN.Scanners.
 *   2. POST /rest/auth/mint-scanner-sid-cookie  (admin's f2_id cookie is
 *      sent automatically; the endpoint mints a sid carrying a partial
 *      bundle scoped to the admin's identity).
 *   3. Display the returned url + sid; offer "Open in new tab" + "Embed
 *      in iframe" actions.
 *   4. Optionally POST /rest/auth/redeem-session to verify the sid is
 *      redeemable (single-use — redeeming here makes a subsequent SPA
 *      redeem fail; toggle off when actually loading the iframe).
 *
 * The iframe is rendered with explicit sandbox attrs so cross-origin
 * scripting from the embedded SPA stays contained.
 */

interface MintRequest {
  url: string;
  method: string;
  body: any;               // human-readable preview (tokens truncated for display)
  bodyRaw?: any;           // wire-shape body — full untruncated tokens; used by copyCurl
  withCredentials: boolean;
}

interface MintResponse {
  status: number | null;
  ok: boolean;
  body: any;
  error?: string;
  durationMs?: number;
}

interface ScannerRow {
  Id: string;
  Name: string;
  Client?: string;
  Url?: string;
  AppUrl?: string;
  SsoAliases?: Record<string, string>;
}

// Integration guides co-located with this component (PDFs in this folder).
// angular.json glob `*.pdf` from `src/app/admin/sso-tester` → output `/docs/`
// at build time. Adding a new guide: drop the PDF here, append a row below.
// Header strip lists only GENERIC / cross-customer guides. Per-customer
// guides surface in the Selected Customer block via cust.docs_url (one
// click away after the operator picks the customer, no header clutter).
const INTEGRATION_GUIDES: ReadonlyArray<{ label: string; href: string }> = [
  {
    label: 'Generic SSO Integration v2',
    href: '/docs/sso-integration-guide-v2.md',
  },
  {
    label: 'Simpler Trading — Big 3 (v1 / deprecated)',
    href: '/docs/sso-integration-guide-simpler-trading.pdf',
  },
];

// Summary row for the Portal-path customer dropdown — populated by
// /rest/admin/customer-directory/sso-portal/list. Mirrors the shape returned
// by CustomerDirectoryService.listSsoPortalEnabled().
export interface PermsSchemaField {
  key: string;
  label: string;
  type: 'string' | 'boolean' | 'enum';
  options?: string[];
  default?: string | boolean;
  help?: string;
}

interface ScannerSelection {
  mode: 'fixed' | 'from_jwt' | 'from_membership';
  scanner_claim?: string;
  membership_map?: Record<string, string>;
}

interface IdentityMapping {
  email?: string;
  email_template?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  // v2 additions
  phone?: string;
  base64_fields?: string[];
}

interface PortalCustomerRow {
  slug: string;
  name: string;
  // True when the row was included ONLY because of MTA federation
  // (mta_endpoint_allowed=true OR _id==='mta') and has no sso_portal.enabled
  // config. Portal-sid / portal-token auth paths get disabled for these
  // rows; the mta-endpoint auth path is enabled.
  mta_federation?: boolean;
  // True when the customer's Mongo row has mta_endpoint_allowed=true OR
  // is the MTA customer itself (implicit). Drives whether the mta-endpoint
  // auth path radio is enabled at all.
  mta_endpoint_allowed?: boolean;
  // sso_portal-derived fields — for mta_federation rows the backend still
  // returns empty defaults (empty strings / [] / 0) so downstream template
  // + `.length` / `.includes` calls don't need per-site guards.
  expected_type: string;
  required_claims: string[];
  allowed_memberships: string[];
  scanner_id: string;
  scanner_selection?: ScannerSelection;
  allowed_scanners?: string[];
  embed_modes: ('iframe' | 'redirect')[];
  scanner_host: string | null;
  docs_url: string | null;
  active_secrets_count: number;
  newest_fingerprint: string | null;
  perms_schema?: PermsSchemaField[];
  claim_template?: Record<string, string>;
  identity_mapping?: IdentityMapping;
  // Customer.claims_schema — drives the embedded ClaimsEditorComponent
  // popup. Same schema the Users admin grid uses.
  claims_schema?: any;
  scanner_ids?: string[];
  // v2 additions
  optional_claims?: string[];
  allow_create_account_enabled?: boolean;
  max_iat_age_seconds?: number;
  sid_ttl_seconds?: number;
  // Iframe-refresh grace window (commit 21900fc). When > 0 the same sid
  // is redeemable for this many additional seconds after the first
  // successful redeem (backend EXPIRE LT). Tester uses this to decide
  // whether sidConsumed should flip true on first redeem — under grace,
  // the SID is still alive and the operator can re-test the round trip.
  sid_redeem_grace_seconds?: number;
  // Handoff URL shape this customer's SPA consumes (commit e189f93).
  //   'sid' (default) → backend emits `?sid=<43char>` (Redis-backed redeem)
  //   'token_query'   → backend emits `?token=<F2-signed-jwt>` (no SID mint;
  //                     JWT signed with this customer's stored HS256 secret)
  handoff_mode?: 'sid' | 'token_query';
  // Per-customer SSO validate debug flag. When true, each successful
  // step of /rest/auth/validate-portal-token/<slug> emits an
  // SSO_DEBUG_<STEP> ErrorReports row. Toggled via the Debug pill
  // in the customer info section. Mike 2026-07-07.
  debug_mode?: boolean;
}

@Component({
  standalone: true,
  selector: 'app-sso-tester',
  imports: [CommonModule, FormsModule, RouterLink, ClaimsEditorComponent],
  templateUrl: './sso-tester.component.html',
})
export class SsoTesterComponent implements OnInit {

  readonly integrationGuides = INTEGRATION_GUIDES;

  scanners: ScannerRow[] = [];
  selectedScannerId = '';
  // Apex endpoint used for both POSTs; lives on f2-admin-service2.
  readonly apiBase: string = `${env.apiUrl}/rest`;

  // Last mint result
  mintReq: MintRequest | null = null;
  mintRes: MintResponse | null = null;
  minting = false;

  // SID lifecycle for the "Open Tab" + "Embed Iframe" buttons. The SID is
  // single-use AND time-limited (90s default per customer config). Both
  // buttons disable when EITHER:
  //   - the timer elapses (sidExpiresAt < now), OR
  //   - one of them was clicked (sidConsumed = true) — opening in any
  //     surface consumes the SID, so the other surface would 404 the redeem.
  // Re-mint (Sign + Validate / remint button) resets both.
  sidExpiresAt: number | null = null;
  sidConsumed = false;
  // Driver for the live countdown — bumped every 1s by an interval started
  // in ngOnInit. Without this, Angular's CD wouldn't re-evaluate the
  // sidRemainingSeconds getter so the disabled state would stick.
  private _nowTick = Date.now();
  private _sidTickInterval: any = null;
  get sidRemainingSeconds(): number {
    if (!this.sidExpiresAt) return 0;
    return Math.max(0, Math.ceil((this.sidExpiresAt - this._nowTick) / 1000));
  }
  get sidExpired(): boolean {
    return !!this.sidExpiresAt && this._nowTick >= this.sidExpiresAt;
  }
  get sidButtonsDisabled(): boolean {
    return !this.mintRes?.body?.url || this.sidConsumed || this.sidExpired;
  }

  /**
   * When the CURRENTLY-SIGNED portal JWT stops being fresh, as unix ms.
   * Prefers the JWT's explicit `exp` claim; falls back to
   * `iat + Customer.sso_portal.max_iat_age_seconds` for minimal-payload
   * customers (theo-trade signs iat only and relies on the backend's
   * freshness window). Returns null when there's no token yet or the
   * decode failed. Used by the countdown badge next to the Mint Request
   * copy buttons (Mike 2026-07-08).
   */
  get portalTokenExpiresAtMs(): number | null {
    if (!this.portalSignedToken) return null;
    const claims = this.decodeJwt(this.portalSignedToken);
    if (!claims) return null;
    if (typeof claims.exp === 'number') return claims.exp * 1000;
    if (typeof claims.iat !== 'number') return null;
    const maxAge =
      (this.selectedPortalCustomer?.max_iat_age_seconds && this.selectedPortalCustomer.max_iat_age_seconds > 0)
        ? this.selectedPortalCustomer.max_iat_age_seconds
        : 60;
    return (claims.iat + maxAge) * 1000;
  }
  get portalTokenRemainingSeconds(): number {
    const at = this.portalTokenExpiresAtMs;
    if (!at) return 0;
    return Math.max(0, Math.ceil((at - this._nowTick) / 1000));
  }
  get portalTokenExpired(): boolean {
    const at = this.portalTokenExpiresAtMs;
    return !!at && this._nowTick >= at;
  }

  // Effective handoff mode — derived from the URL the backend returned.
  // Truth source: the customer's sso_portal.handoff_mode (mirrored on the
  // returned URL shape). 'sid' if the URL has ?sid=, 'token_query' if
  // ?token=. Used for mode-aware UX: hide redeem toggle in token mode,
  // decode outbound JWT, show mode badge, label countdown source.
  get returnedHandoffMode(): 'sid' | 'token_query' | null {
    const url = this.mintRes?.body?.url;
    if (!url || typeof url !== 'string') return null;
    if (url.includes('?token=') || url.includes('&token=')) return 'token_query';
    if (url.includes('?sid=') || url.includes('&sid=')) return 'sid';
    return null;
  }
  // Decoded payload of the F2-signed outbound JWT (token_query mode only).
  // null when not in token mode or when the URL doesn't carry a parseable
  // token. Operator inspects this to confirm what F2 emitted to the
  // customer SPA. Current shape (TheoTrade v2 minimal, symmetric with the
  // inbound portal JWT): { email (base64), first_name, last_name,
  // phone (base64, optional), iat, allow_create_account }. NO sid claim —
  // the redirect's JWT is a portable signed identity assertion.
  get outboundJwtPayload(): any {
    if (this.returnedHandoffMode !== 'token_query') return null;
    const url = this.mintRes?.body?.url;
    if (!url) return null;
    try {
      const u = new URL(url);
      const token = u.searchParams.get('token');
      if (!token) return null;
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const pad = (s: string) => s + '='.repeat((4 - s.length % 4) % 4);
      const json = atob(pad(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return JSON.parse(json);
    } catch { return null; }
  }
  // Pretty-printed JSON for the outbound JWT payload preview.
  get outboundJwtJson(): string {
    const p = this.outboundJwtPayload;
    return p ? JSON.stringify(p, null, 2) : '';
  }

  // Last redeem result (uses single-use sid — keep behind a toggle so the
  // admin doesn't accidentally invalidate the sid they want to embed).
  redeemAlsoTest = false;
  redeemReq: MintRequest | null = null;
  redeemRes: MintResponse | null = null;
  redeeming = false;

  // Token-mode round-trip: simulate the consumer SPA exchanging the emitted
  // `?token=<jwt>` URL credential for the Cognito bundle via
  // /auth/exchange-portal-token. Closes the loop on the outbound JWT — proves
  // not just that F2 emitted a well-formed JWT but that the same JWT verifies
  // server-side AND its inner sid claim still redeems against Redis. Like the
  // redeem flow, this consumes the underlying single-use sid — subsequent
  // open/embed of the same URL will fail.
  exchangeReq: MintRequest | null = null;
  exchangeRes: MintResponse | null = null;
  exchanging = false;

  // Iframe state. iframeSrc is the raw URL (for display); iframeSafeSrc
  // is the same URL run through DomSanitizer so [src] binding doesn't
  // get blocked.
  iframeSrc: string | null = null;
  iframeSafeSrc: SafeResourceUrl | null = null;
  iframeKey = 0;  // bump to force re-render with a fresh sid
  iframeHeight: number = 720;

  // ----- Auth path selection (Step 2) -----
  // 'admin'    = use the admin's f2_id cookie (mint-scanner-sid-cookie)
  // 'customer' = log in first as a regular user, then mint with their
  //              bundle (mint-scanner-sid). This simulates exactly what
  //              an iframe / SSO integration on the customer side does.
  // 'portal'   = JWTSSO portal handoff (PDF "Scanner SSO Integration Guide").
  //              Server signs a test JWT with the customer's secret env var,
  //              then POSTs it to /rest/auth/validate-portal-token/:customer.
  //              Driven entirely by F2-ADMIN.Customers.sso_portal config.
  // 'portal-sid' and 'portal-token' both simulate a customer-portal JWTSSO
  // handoff; they differ ONLY in which validator endpoint the tester POSTs
  // the signed JWT to (and therefore which delivery shape comes back):
  //   'portal-sid'   → /auth/validate-portal-token/:customer (force_sid=true
  //                    server-side; ALWAYS returns ?sid= URL)
  //   'portal-token' → /auth/validate-customer-token { customer, token }
  //                    (honors customer's handoff_mode → ?token= URL when
  //                    handoff_mode='token_query'; otherwise also ?sid=)
  // 'portal-token' is only meaningful when the customer has token_query
  // enabled — when not, the step-2 radio for it is disabled with a hint.
  // 'mta-endpoint' exercises MTA's federated endpoint:
  //     POST /rest/auth/validate-mta-token { token }
  //   Token is minted server-side using env.MTA_JWT_SECRET_KEY (MTA's shared
  //   secret) via /rest/admin/customer-directory/sign-mta-test-jwt. The
  //   endpoint routes to whichever customer owns the JWT's `scanner` claim
  //   (F2-ADMIN.Scanners.<id>.Client — plus a Customers.<slug>.
  //   mta_endpoint_allowed=true or _id==='mta' gate). Radio disabled when
  //   the selected customer doesn't satisfy that gate.
  authPath: 'admin' | 'customer' | 'portal-sid' | 'portal-token' | 'mta-endpoint' = 'admin';
  // Convenience: is the path one of the two portal flavors? Replaces the
  // many `authPath === 'portal'` checks scattered through the template +
  // component, kept stable across the split.
  get isPortalPath(): boolean {
    return this.authPath === 'portal-sid' || this.authPath === 'portal-token';
  }
  // True when the operator picked the MTA-endpoint auth path. Kept as a
  // getter so template + component code can gate on a single expression.
  get isMtaPath(): boolean {
    return this.authPath === 'mta-endpoint';
  }
  // Whether the currently-selected customer satisfies the MTA-endpoint
  // gate — either _id==='mta' (implicit) or mta_endpoint_allowed=true.
  // Drives enable-state of the mta-endpoint radio + the "you must pick MTA"
  // hint on portal-sid / portal-token for mta_federation-only customers.
  get portalCanTestMtaEndpoint(): boolean {
    const c = this.selectedPortalCustomer;
    return !!c && (c.mta_endpoint_allowed === true || c.slug === 'mta' || c.mta_federation === true);
  }

  // Customer-side credentials state.
  custEmail = '';
  custPassword = '';
  loginReq: MintRequest | null = null;
  loginRes: MintResponse | null = null;
  logging_in = false;
  // Bundle returned by /auth/login. Held in memory only; clearing the
  // form clears this. NEVER persisted.
  custBundle: any = null;

  // ----- Portal-path state (JWTSSO handoff) -----
  // Populated on ngOnInit from /rest/admin/customer-directory/sso-portal/list.
  // Each row drives the per-customer form (memberships checkboxes, scanner_id
  // display, expected redirect preview).
  portalCustomers: PortalCustomerRow[] = [];
  // Transient — set true while the Debug toggle POST is in flight so
  // the checkbox disables + shows a spinner. Never persisted.
  portalDebugTogglePending = false;
  selectedPortalSlug = '';
  // Inputs the admin fills in to mint a test JWT. The server fills in
  // type / iat / exp / signing.
  portalEmail = '';
  portalName = '';
  // Separate first/last for identity_mapping customers (TheoTrade-style).
  portalFirstName = '';
  portalLastName = '';
  // For email_template (MTA-style) — operator types the source field
  // (e.g. user_identifier) and the server interpolates into the template.
  portalEmailSourceClaim = '';   // detected from identity_mapping.email_template
  portalEmailSourceValue = '';
  // When scanner_selection.mode === 'from_jwt', operator picks ONE or MORE
  // of this customer's scanners. First becomes the JWT's `scanner` claim
  // (landing target). All become the `scanners[]` entitlement array.
  // Real users typically have access to multiple scanners — TheoTrade's
  // JWT contract sends both fields, so the tester does too.
  portalScannerAliases: Record<string, boolean> = {};
  // Generic JSON escape hatch for non-JWTSSO extras (live_data, scanners[],
  // user_identifier, etc.). Passed through verbatim to the JWT payload.
  portalExtrasJson = '{}';
  // v2: per-JWT allow_create_account toggle. Only rendered + sent when the
  // customer's config sets allow_create_account_enabled. When the
  // checkbox is OFF, first_name/last_name/phone fields hide + aren't
  // required (lookup-only mode); when ON, those fields become required so
  // the operator can supply what F2 needs to mint a new Cognito user.
  portalAllowCreateAccount = false;
  // SECURITY: dry-run is the default. When ON, the validator runs all
  // checks but does NOT write to Cognito / mint a sid / create users —
  // returns the would-be entitlement diff for the operator to inspect.
  // Turning it OFF requires an explicit confirmation modal (see
  // toggleDryRun) because real writes mutate production state for the
  // target email. Closes the SSO Tester write-amplification bug where
  // operators could silently grant entitlements to any typed email.
  portalDryRun = true;
  // Set during the confirm modal flow to remember which target the
  // operator just approved. Cleared after the mint completes.
  pendingGrantConfirm: { customer: string; email: string; scanner: string } | null = null;
  // Append-only event log capturing what the operator did + what came back.
  // Powers the "Copy Debug Bundle" button at the top of the page — when
  // something behaves unexpectedly the operator can paste the bundle and
  // the dev gets the full repro: sequence of clicks, request/response
  // pairs, current form state, mode toggles. Capped at 200 entries to
  // bound memory (this is in-tab; cleared on page refresh).
  debugEvents: Array<{ ts: string; kind: string; msg: string; data?: any }> = [];
  private _logEvent(kind: string, msg: string, data?: any) {
    const ts = new Date().toISOString();
    this.debugEvents.push({ ts, kind, msg, data });
    if (this.debugEvents.length > 200) this.debugEvents.splice(0, this.debugEvents.length - 200);
  }
  // v2: optional phone field. Hidden unless customer has identity_mapping.phone
  // configured AND portalAllowCreateAccount is true. Plaintext input —
  // tester base64-encodes at sign time if customer has phone in base64_fields.
  portalPhone = '';
  // Scanner-slug field for the JWT's `scanner` payload field. When set,
  // the backend resolves the redirect URL to that specific scanner;
  // when blank, the landing is the customer's members page. Independent
  // of customer scanner_selection mode — backend prefers payload.scanner
  // whenever present.
  portalScannerSlug = '';
  // Operator-chosen signing-secret id. Empty = newest (default backend
  // behavior). Populated by the dropdown built from secretSummaries.
  portalSigningSecretId = '';
  // When true (default when the selected customer has scanner_host set),
  // the mint POST is directed at https://<customer.scanner_host>/rest/…
  // instead of the admin backend host. Matches the URL shape the
  // customer's production integration uses — so a customer-host misconfig
  // (missing Vercel rewrite, DNS, edge cache) surfaces in the tester
  // instead of at 3am from a Laravel handler. Mike 2026-07-08 ask:
  // "our sso tester should support customer domain option by default if
  // the customer has one." Radio-visible in Step 3.
  usePortalCustomerHost = false;
  // IT-F2-437 Mike 2026-10-07 cid:6718e44b: omit the `/<slug>` suffix on
  // the validate URL and let the backend derive customer from the Host /
  // X-Forwarded-Host / Origin header (backend PR #231). Lets the tester
  // exercise the branded-host-no-slug shape the TheoTrade customer SSO
  // uses post f2-session's Edge Middleware passthrough (IT-F2-437 item
  // 61e1469e). Only meaningful when `usePortalCustomerHost=true` AND
  // the customer has a scanner_host; forced false otherwise so the
  // admin.f2-tech.ai host doesn't resolve a mismatched customer.
  omitPortalSlug = false;
  // Scanner input mode for the portal path. 'jwt' (default) = include the
  // scanner as a payload claim, backend reads it from the JWT (existing
  // behavior). 'url' = don't include scanner in the JWT; put the scanner
  // id in the URL path via the /validate-{portal,customer}-token/:slug
  // overload (backend commits 4aabba3 + c4cc8d1, 2026-07-08). Requires a
  // valid scanner to be picked in portalScannerSlug; falls back to the
  // customer slug in the URL when scanner is blank. Mike 2026-07-08 ask.
  portalScannerInputMode: 'jwt' | 'url' = 'jwt';
  // True when the selected customer has the ?token= delivery shape enabled
  // on their sso_portal. Drives the enabled/disabled state of the
  // 'portal-token' radio in step 2: only customers with handoff_mode
  // ='token_query' can be exercised on /validate-customer-token in a way
  // that returns a token-shaped URL. Sid-only customers see that radio
  // greyed out with an explanatory hint.
  get portalCanTestTokenMode(): boolean {
    return this.selectedPortalCustomer?.handoff_mode === 'token_query';
  }
  // Per-customer CustomerAssets docs surfaced in the top "Integration
  // guides" strip. Fetched from /rest/admin/customer-assets/<slug> when
  // a customer is selected so the strip reflects the docs we actually
  // ship to THIS customer (integration guides, internal runbooks),
  // not the static cross-customer placeholders. Empty when no customer
  // is selected — falls back to the static INTEGRATION_GUIDES list.
  customerAssetLinks: Array<{ label: string; href: string; filename: string; visibility: string[] }> = [];
  private async _refreshCustomerAssetLinks() {
    const slug = this.selectedPortalCustomer?.slug;
    if (!slug) { this.customerAssetLinks = []; return; }
    try {
      const res = await this.http.get(
        `${this.apiBase}/admin/customer-assets/${encodeURIComponent(slug)}`,
        { withCredentials: true },
      ).toPromise() as any[];
      if (!Array.isArray(res)) { this.customerAssetLinks = []; return; }
      this.customerAssetLinks = res.map((a: any) => ({
        // Prefer label (operator-set display title); fall back to filename.
        label: String(a.label || a.filename || a.id),
        // Admin-cookie serve URL. The endpoint canRead-gates on userData
        // so admin sees every asset; clicking opens in a new tab and the
        // F2 admin login cookie travels along (same eTLD+1).
        href: `${this.apiBase}/admin/customer-assets/${encodeURIComponent(slug)}/${encodeURIComponent(a.id)}`,
        filename: String(a.filename || ''),
        visibility: Array.isArray(a.visibility) ? a.visibility : [],
      }));
    } catch {
      this.customerAssetLinks = [];
    }
  }
  // Per-customer list of available signing secrets (id + label +
  // fingerprint + created_at). Fetched on customer change so the
  // dropdown reflects the live set; refreshed when the operator
  // generates/imports/deletes a secret in the popup elsewhere.
  portalAvailableSecrets: Array<{ id: string; label?: string; fingerprint: string; length: number; created_at: string }> = [];
  private async _refreshPortalAvailableSecrets() {
    const slug = this.selectedPortalCustomer?.slug;
    if (!slug) { this.portalAvailableSecrets = []; return; }
    try {
      const res = await this.http.get(`${this.apiBase}/admin/customer-directory/${encodeURIComponent(slug)}/sso-portal/secrets`, { withCredentials: true }).toPromise() as any[];
      this.portalAvailableSecrets = Array.isArray(res) ? res : [];
    } catch {
      this.portalAvailableSecrets = [];
    }
  }
  // Checkbox selection over the customer's allowed_memberships list. Key =
  // membership slug, value = true when included in the JWT payload.
  portalMemberships: Record<string, boolean> = {};
  portalPermsJson = '{}';
  portalExpSeconds = 60;
  // Embedded ClaimsEditorComponent (the shared one used by the Users
  // admin grid). One source of truth for claim shape across surfaces.
  // claimsEditorJson is the JSON string the editor reads/writes; on
  // save the first entry's non-scanner fields become JWT top-level
  // claims for the tester. The customer's claims_schema is the contract.
  claimsEditorOpen = false;
  claimsEditorJson = '[]';
  // Cached customer-shape input for ClaimsEditor. Must be a STABLE
  // reference (re-built only when the selected portal customer changes),
  // not a getter — getters create a new array reference per CD tick,
  // which makes ClaimsEditor's ngOnChanges fire every tick → entries
  // rebuild → inputs lose focus → page appears frozen.
  claimsEditorCustomers: any[] = [];
  // The "perms preview" pill in the form is a JSON of what'll be sent.
  // Kept readable so the operator can spot-check without opening the editor.
  get permsPreview(): string { return this.portalPermsJson; }
  // Sign-token request/response (Step 3a in the portal flow).
  signTokenReq: MintRequest | null = null;
  signTokenRes: MintResponse | null = null;
  signing_token = false;
  // The signed JWT held in memory only; feeds the validate call.
  portalSignedToken = '';

  // ----- MTA-endpoint state -----
  // Fields the operator supplies for the test JWT payload. MTA's
  // production portal signs with these three shape claims (see auth
  // service `_validate_mta_token`, required list: user_identifier,
  // access_tier, scanner, iat, exp). iat + exp auto-set server-side.
  // Prefill sensibly so the operator can go from "picked MTA-endpoint"
  // to "click Sign" without typing when they only want to verify the
  // wire round-trip. Scanner claim defaults to the first mta-eligible
  // scanner the customer owns; user_identifier is a stable synthetic;
  // access_tier defaults to 1 (paying customer, most common).
  mtaUserIdentifier = 'sso-tester';
  mtaAccessTier = 1;
  mtaScannerClaim = '';
  // Operator-editable JWT lifetime. Backend clamps [5, 900] and defaults
  // to 300 when unspecified — matching MTA's production ~5min window.
  mtaExpSeconds = 300;
  // Signed JWT + last sign-jwt req/res pair.
  mtaSignedJwt = '';
  mtaSignedPayload: any = null;
  mtaSignFingerprint = '';
  mtaSignReq: MintRequest | null = null;
  mtaSignRes: MintResponse | null = null;
  signing_mta_jwt = false;
  // Live JWT payload preview built from mta* form fields — same pattern as
  // portal path's `jwtPreview`. Lets the operator confirm what will land
  // in the sign request BEFORE clicking Sign.
  get mtaJwtPreview(): any {
    return {
      user_identifier: this.mtaUserIdentifier,
      access_tier: this.mtaAccessTier,
      scanner: this.mtaScannerClaim,
      // iat + exp are backend-injected; show placeholders so the operator
      // sees the shape even though the actual values come from the server.
      iat: '(now — set by server)',
      exp: `(now + ${this.mtaExpSeconds}s — set by server)`,
    };
  }
  get mtaJwtPreviewJson(): string {
    return JSON.stringify(this.mtaJwtPreview, null, 2);
  }
  // True when the MTA form has everything Sign needs. Scanner is required
  // because auth.service._validate_mta_token errors "invalid scanner:
  // (empty)" without it — surface the gate before we round-trip a
  // known-bad payload.
  get mtaReady(): boolean {
    if (!this.selectedPortalCustomer) return false;
    if (!this.portalCanTestMtaEndpoint) return false;
    if (!this.mtaUserIdentifier.trim()) return false;
    if (!this.mtaScannerClaim.trim()) return false;
    return true;
  }

  @ViewChild('iframeEl') iframeEl?: ElementRef<HTMLIFrameElement>;

  constructor(
    public authService: AuthService,
    private scannersService: ScannersService,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private toast: ToastService,
  ) {}

  // localStorage key for form-state persistence across page refreshes.
  // Bump the version suffix when the persisted shape changes incompatibly
  // (don't try to migrate — let the old key go stale + drop on next load).
  private readonly _SETTINGS_KEY = 'f2admin.sso-tester.settings.v1';
  // Auto-save tick: writes the current form snapshot every 2 seconds when
  // any persistable field has changed. Cheap (single JSON.stringify) and
  // means the operator never loses input even on hard tab-close. Cleared
  // on component destroy.
  private _autoSaveInterval: any = null;
  // beforeunload listener bound in ngOnInit so we can detach in ngOnDestroy.
  // Last-chance save before the tab actually closes.
  private _beforeUnloadHandler: (() => void) | null = null;

  ngOnDestroy(): void {
    if (this._sidTickInterval) clearInterval(this._sidTickInterval);
    this._sidTickInterval = null;
    if (this._autoSaveInterval) clearInterval(this._autoSaveInterval);
    this._autoSaveInterval = null;
    if (this._beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this._beforeUnloadHandler);
      this._beforeUnloadHandler = null;
    }
    // Final save on destroy — covers route-away navigation within the SPA.
    this._saveSettings();
  }

  async ngOnInit(): Promise<void> {
    // 1-second tick drives the SID countdown + auto-disable. Cheap; no
    // re-renders unless the rendered value actually changes (text inside
    // the [data-binding]). Cleared on component destroy.
    this._sidTickInterval = setInterval(() => { this._nowTick = Date.now(); }, 1000);

    // Restore persisted form state FIRST so subsequent async loads
    // (scanners, portal customers) don't overwrite operator-typed values.
    this._loadSettings();

    // Auto-save loop + beforeunload guard so input survives refresh / tab-close.
    this._autoSaveInterval = setInterval(() => this._saveSettings(), 2000);
    this._beforeUnloadHandler = () => this._saveSettings();
    window.addEventListener('beforeunload', this._beforeUnloadHandler);

    const res: any = await this.scannersService.getScanners();
    if (res && !res.err) {
      this.scanners = Array.isArray(res) ? res : (res?.scanners || []);
      // Sort by Client then Name so the dropdown is browsable.
      this.scanners.sort((a, b) => {
        const c = (a.Client || '').localeCompare(b.Client || '');
        if (c !== 0) return c;
        return (a.Name || '').localeCompare(b.Name || '');
      });
    }
    // Portal-path: load the customers that have sso_portal.enabled = true.
    // Quiet failure — the radio button stays disabled with an empty list.
    try {
      const portalRes = await this.http.get(
        `${this.apiBase}/admin/customer-directory/sso-portal/list`,
        { withCredentials: true }
      ).toPromise() as any;
      if (Array.isArray(portalRes)) this.portalCustomers = portalRes;
    } catch { /* silent — UI handles empty list */ }

    // Re-hydrate customer-dependent caches when a customer was restored
    // from settings. Without this the scanner dropdown stays empty (and
    // the claims-editor host array, secrets dropdown, etc.) until the
    // operator re-picks the customer — exactly the friction the persist
    // feature was supposed to remove. Caches depend on BOTH the global
    // scanners catalog AND the portalCustomers list being loaded, which
    // is why this runs at the END of ngOnInit after both awaits resolve.
    if (this.selectedPortalSlug && this.portalCustomers.some(c => c.slug === this.selectedPortalSlug)) {
      this._rebuildPortalScannerOptions();
      this._rebuildClaimsEditorCustomers();
      this._refreshPortalAvailableSecrets();
    }
  }

  // Snapshot persistable form fields → localStorage. Skip ephemeral state
  // (requests/responses, bundles, iframe state, events, password inputs)
  // and skip security-sensitive defaults (portalDryRun always re-defaults
  // to true on load so a refresh can't silently leave an operator in REAL
  // grant mode).
  // Public so templates can call it from (ngModelChange) hooks —
  // immediate save for toggles the operator will want to survive an
  // F5 that beats the 2s auto-save interval (Mike 2026-07-08 report:
  // customer-host / scanner-mode toggles not remembered on refresh).
  _saveSettings() {
    try {
      const snapshot = {
        authPath: this.authPath,
        selectedScannerId: this.selectedScannerId,
        redeemAlsoTest: this.redeemAlsoTest,
        custEmail: this.custEmail,
        selectedPortalSlug: this.selectedPortalSlug,
        portalEmail: this.portalEmail,
        portalName: this.portalName,
        portalFirstName: this.portalFirstName,
        portalLastName: this.portalLastName,
        portalEmailSourceClaim: this.portalEmailSourceClaim,
        portalEmailSourceValue: this.portalEmailSourceValue,
        portalPhone: this.portalPhone,
        portalAllowCreateAccount: this.portalAllowCreateAccount,
        portalExpSeconds: this.portalExpSeconds,
        portalSigningSecretId: this.portalSigningSecretId,
        portalMemberships: this.portalMemberships,
        portalScannerAliases: this.portalScannerAliases,
        portalPermsJson: this.portalPermsJson,
        portalExtrasJson: this.portalExtrasJson,
        claimsEditorJson: this.claimsEditorJson,
        portalScannerSlug: this.portalScannerSlug,
        usePortalCustomerHost: this.usePortalCustomerHost,
        omitPortalSlug: this.omitPortalSlug,
        portalScannerInputMode: this.portalScannerInputMode,
        iframeHeight: this.iframeHeight,
        // MTA-endpoint form
        mtaUserIdentifier: this.mtaUserIdentifier,
        mtaAccessTier: this.mtaAccessTier,
        mtaScannerClaim: this.mtaScannerClaim,
        mtaExpSeconds: this.mtaExpSeconds,
      };
      localStorage.setItem(this._SETTINGS_KEY, JSON.stringify(snapshot));
    } catch { /* localStorage full / unavailable / Safari private — silent */ }
  }

  private _loadSettings() {
    try {
      const raw = localStorage.getItem(this._SETTINGS_KEY);
      if (!raw) return;
      const s = JSON.parse(raw);
      // Field-by-field with type guards so a corrupted/older snapshot
      // can't crash the component on load.
      // Migrate the historical 'portal' value to 'portal-sid' (the sid-only
      // path that matches the previous behavior of /validate-portal-token
      // post-d7365fc). 'portal-token' is a new explicit opt-in.
      if (s.authPath === 'admin' || s.authPath === 'customer') {
        this.authPath = s.authPath;
      } else if (s.authPath === 'portal-sid' || s.authPath === 'portal-token' || s.authPath === 'mta-endpoint') {
        this.authPath = s.authPath;
      } else if (s.authPath === 'portal') {
        this.authPath = 'portal-sid';
      }
      if (typeof s.selectedScannerId === 'string') this.selectedScannerId = s.selectedScannerId;
      if (typeof s.redeemAlsoTest === 'boolean') this.redeemAlsoTest = s.redeemAlsoTest;
      if (typeof s.custEmail === 'string') this.custEmail = s.custEmail;
      if (typeof s.selectedPortalSlug === 'string') this.selectedPortalSlug = s.selectedPortalSlug;
      if (typeof s.portalEmail === 'string') this.portalEmail = s.portalEmail;
      if (typeof s.portalName === 'string') this.portalName = s.portalName;
      if (typeof s.portalFirstName === 'string') this.portalFirstName = s.portalFirstName;
      if (typeof s.portalLastName === 'string') this.portalLastName = s.portalLastName;
      if (typeof s.portalEmailSourceClaim === 'string') this.portalEmailSourceClaim = s.portalEmailSourceClaim;
      if (typeof s.portalEmailSourceValue === 'string') this.portalEmailSourceValue = s.portalEmailSourceValue;
      if (typeof s.portalPhone === 'string') this.portalPhone = s.portalPhone;
      if (typeof s.portalAllowCreateAccount === 'boolean') this.portalAllowCreateAccount = s.portalAllowCreateAccount;
      if (typeof s.portalExpSeconds === 'number') this.portalExpSeconds = s.portalExpSeconds;
      if (typeof s.portalSigningSecretId === 'string') this.portalSigningSecretId = s.portalSigningSecretId;
      if (s.portalMemberships && typeof s.portalMemberships === 'object') this.portalMemberships = s.portalMemberships;
      if (s.portalScannerAliases && typeof s.portalScannerAliases === 'object') this.portalScannerAliases = s.portalScannerAliases;
      if (typeof s.portalPermsJson === 'string') this.portalPermsJson = s.portalPermsJson;
      if (typeof s.portalExtrasJson === 'string') this.portalExtrasJson = s.portalExtrasJson;
      if (typeof s.claimsEditorJson === 'string') this.claimsEditorJson = s.claimsEditorJson;
      if (typeof s.portalScannerSlug === 'string') this.portalScannerSlug = s.portalScannerSlug;
      if (typeof s.usePortalCustomerHost === 'boolean') this.usePortalCustomerHost = s.usePortalCustomerHost;
      if (typeof s.omitPortalSlug === 'boolean') this.omitPortalSlug = s.omitPortalSlug;
      if (s.portalScannerInputMode === 'url' || s.portalScannerInputMode === 'jwt') {
        this.portalScannerInputMode = s.portalScannerInputMode;
      }
      // Clamp iframeHeight to the same 240..2000 range the template <input>
      // enforces so a corrupted / hand-edited snapshot can't push the
      // iframe off-screen. Missing / non-number falls through to the
      // 720 default from the class-level initializer.
      if (typeof s.iframeHeight === 'number' && s.iframeHeight >= 240 && s.iframeHeight <= 2000) {
        this.iframeHeight = s.iframeHeight;
      }
      // MTA-endpoint form
      if (typeof s.mtaUserIdentifier === 'string') this.mtaUserIdentifier = s.mtaUserIdentifier;
      if (typeof s.mtaAccessTier === 'number') this.mtaAccessTier = s.mtaAccessTier;
      if (typeof s.mtaScannerClaim === 'string') this.mtaScannerClaim = s.mtaScannerClaim;
      if (typeof s.mtaExpSeconds === 'number' && s.mtaExpSeconds >= 5 && s.mtaExpSeconds <= 900) {
        this.mtaExpSeconds = s.mtaExpSeconds;
      }
    } catch { /* corrupted JSON / localStorage unavailable — silent reset */ }
  }

  // Wipe ALL state and the persisted snapshot — operator hits "Clear Form"
  // when they want a clean slate. Includes form inputs, request/response
  // panels, bundles, sid lifecycle, iframe, AND the event log so a new
  // debug session starts at zero events.
  clearForm() {
    // Form inputs back to defaults
    this.authPath = 'admin';
    this.selectedScannerId = '';
    this.redeemAlsoTest = false;
    this.custEmail = '';
    this.custPassword = '';
    this.custBundle = null;
    this.selectedPortalSlug = '';
    this.portalEmail = '';
    this.portalName = '';
    this.portalFirstName = '';
    this.portalLastName = '';
    this.portalEmailSourceClaim = '';
    this.portalEmailSourceValue = '';
    this.portalPhone = '';
    this.portalAllowCreateAccount = false;
    this.portalDryRun = true;
    this.portalExpSeconds = 60;
    this.portalSigningSecretId = '';
    this.portalMemberships = {};
    this.portalScannerAliases = {};
    this.portalPermsJson = '{}';
    this.portalExtrasJson = '{}';
    this.claimsEditorJson = '[]';
    this.portalScannerSlug = '';
    this.portalAvailableSecrets = [];
    this.pendingGrantConfirm = null;
    // Request/response panels
    this.signTokenReq = null;
    this.signTokenRes = null;
    this.signing_token = false;
    this.portalSignedToken = '';
    this.loginReq = null;
    this.loginRes = null;
    this.logging_in = false;
    this.mintReq = null;
    this.mintRes = null;
    this.minting = false;
    this.redeemReq = null;
    this.redeemRes = null;
    this.redeeming = false;
    this.exchangeReq = null;
    this.exchangeRes = null;
    this.exchanging = false;
    // MTA-endpoint state
    this.mtaUserIdentifier = 'sso-tester';
    this.mtaAccessTier = 1;
    this.mtaScannerClaim = '';
    this.mtaExpSeconds = 300;
    this.mtaSignedJwt = '';
    this.mtaSignedPayload = null;
    this.mtaSignFingerprint = '';
    this.mtaSignReq = null;
    this.mtaSignRes = null;
    this.signing_mta_jwt = false;
    // SID / URL / iframe lifecycle
    this.sidExpiresAt = null;
    this.sidConsumed = false;
    this.iframeSrc = null;
    this.iframeSafeSrc = null;
    this.iframeKey = 0;
    // Event log — last so the clear action is the only thing in it.
    this.debugEvents = [];
    this._logEvent('action', 'form cleared (operator hit "Clear Form")');
    // Wipe the persisted snapshot so the next refresh starts blank.
    try { localStorage.removeItem(this._SETTINGS_KEY); } catch { /* silent */ }
  }

  get selectedScanner(): ScannerRow | undefined {
    return this.scanners.find(s => s.Id === this.selectedScannerId);
  }

  // Toggle sso_portal.debug_mode for the currently selected customer.
  // Optimistic-updates the in-memory PortalCustomerRow so the checkbox
  // reflects immediately; rolls back on failure. Backend clears its
  // sso_portal cache so the next validate call picks up the new value.
  async onPortalDebugToggle(enabled: boolean): Promise<void> {
    const cust = this.selectedPortalCustomer;
    if (!cust || this.portalDebugTogglePending) return;
    this.portalDebugTogglePending = true;
    const prev = cust.debug_mode === true;
    cust.debug_mode = enabled === true;   // optimistic
    try {
      const url = `${this.apiBase}/admin/customer-directory/${encodeURIComponent(cust.slug)}/sso-portal/debug-mode`;
      const res: any = await this.http.post(url, { enabled: enabled === true }, { withCredentials: true }).toPromise();
      if (res?.err) {
        cust.debug_mode = prev;  // rollback
        this.toast.error('SSO debug toggle', res.err);
      }
    } catch (e: any) {
      cust.debug_mode = prev;  // rollback
      this.toast.error('SSO debug toggle', e?.message || 'network error');
    } finally {
      this.portalDebugTogglePending = false;
    }
  }

  portalDebugTooltip(cust: PortalCustomerRow): string {
    return cust?.debug_mode
      ? 'SSO debug mode ON — each successful validate step emits an ErrorReports row for this customer'
      : 'Toggle SSO debug mode — see per-step ErrorReports for validate calls';
  }

  get selectedPortalCustomer(): PortalCustomerRow | undefined {
    return this.portalCustomers.find(c => c.slug === this.selectedPortalSlug);
  }

  // Re-seed the per-customer state when the portal customer dropdown
  // changes. EVERY field resets to blank/false/empty — the tester is the
  // raw exercise surface and must NOT pre-fill from customer config (no
  // crutches). Pre-filling masks customer-side bugs by silently supplying
  // values the customer's portal might be omitting.
  onPortalCustomerChange() {
    this._logEvent('change', `customer → ${this.selectedPortalSlug || '(none)'}`);
    this.portalMemberships = {};
    // Rebuild stable references for the customer-dependent inputs (avoids
    // per-tick getter re-allocations that re-mount checkboxes/inputs and
    // freeze the page).
    this._rebuildClaimsEditorCustomers();
    this._rebuildPortalScannerOptions();
    this.claimsEditorJson = '[]';
    this.portalPermsJson = '{}';
    this.portalFirstName = '';
    this.portalLastName = '';
    this.portalEmailSourceClaim = '';
    this.portalEmailSourceValue = '';
    this.portalScannerAliases = {};
    this.portalExtrasJson = '{}';
    this.portalAllowCreateAccount = false;
    this.portalPhone = '';
    this.portalScannerSlug = '';
    this.portalSigningSecretId = '';
    // Default the "use customer host" toggle based on whether the newly-
    // selected customer has a scanner_host configured. Yes → default ON
    // (mint POST goes to customer's own host, matching production traffic
    // shape). No → default OFF (falls back to admin host). Operator can
    // toggle either way per test.
    this.usePortalCustomerHost = !!this.selectedPortalCustomer?.scanner_host;
    // Scanner input mode stays 'jwt' across customer changes — preserves
    // the current tester default; operator opts into 'url' per test.
    this.portalScannerInputMode = 'jwt';
    // If the operator currently has 'portal-token' selected but the new
    // customer doesn't support token_query mode, flip to 'portal-sid' so
    // the disabled radio doesn't strand the form in an un-mintable state.
    if (this.authPath === 'portal-token' && !this.portalCanTestTokenMode) {
      this.authPath = 'portal-sid';
    }
    this._refreshPortalAvailableSecrets();
    this._refreshCustomerAssetLinks();
    // Defense in depth: every fresh customer selection re-enters dry-run
    // mode. Forces the operator to re-confirm the target before any real
    // mutation, even if they were in real grant mode for a different
    // customer moments earlier.
    this.portalDryRun = true;
    this.signTokenReq = null;
    this.signTokenRes = null;
    this.portalSignedToken = '';
    this.mintReq = null;
    this.mintRes = null;
    this.exchangeReq = null;
    this.exchangeRes = null;
    // MTA-endpoint state — reset alongside portal so the customer switch
    // starts fresh. Default the scanner claim to the first scanner this
    // customer owns (the operator can override).
    this.mtaSignedJwt = '';
    this.mtaSignedPayload = null;
    this.mtaSignFingerprint = '';
    this.mtaSignReq = null;
    this.mtaSignRes = null;
    const firstScanner = this.portalCustomerScanners[0]?.id
      || this.selectedPortalCustomer?.scanner_ids?.[0]
      || '';
    this.mtaScannerClaim = firstScanner;
    // If operator was on mta-endpoint but the newly-selected customer
    // isn't eligible, fall back to admin so the form isn't stranded.
    if (this.authPath === 'mta-endpoint' && !this.portalCanTestMtaEndpoint) {
      this.authPath = 'admin';
    }
    // Mirror-image: when the newly-selected customer is mta_federation-only
    // (no sso_portal — the portal-sid / portal-token radios are disabled)
    // AND the operator was on one of those disabled radios, auto-flip to
    // mta-endpoint so a subsequent Sign+Mint click doesn't fire at
    // /validate-portal-token/<slug> and return "no sso_portal configured"
    // (bit Mike ER-D462RBY 2026-09-15 IT-F2-360 on the first oxc pick).
    // Only fires when the customer actually supports mta-endpoint — safe
    // no-op otherwise.
    const isMtaOnly = !!this.selectedPortalCustomer?.mta_federation;
    const onDisabledPortalPath = this.authPath === 'portal-sid' || this.authPath === 'portal-token';
    if (isMtaOnly && onDisabledPortalPath && this.portalCanTestMtaEndpoint) {
      this.authPath = 'mta-endpoint';
    }
  }

  // Scanner picker options for the from_jwt mode — derived from each of
  // the customer's scanners' SsoAliases[customerSlug] entry. When no alias
  // is set, falls back to the scanner's internal _id (the validator
  // accepts both).
  //
  // Must be a STABLE reference (rebuilt only on customer change, NOT a
  // getter) — checkbox *ngFor over a new-each-tick array re-mounts the
  // checkboxes every CD pass, eating clicks and freezing the page.
  portalScannerOptions: Array<{ value: string; label: string }> = [];
  // Concrete scanners for the scanner-slug picker — filtered from the
  // authoritative Scanners collection by Client === customer-slug (NOT
  // the potentially-stale Customer.scanner_ids mirror). Each entry carries
  // both the formal Name (display) and Id (slug, what goes in the JWT).
  // Stable reference rebuilt on customer change so *ngFor doesn't re-mount
  // each CD pass.
  portalCustomerScanners: Array<{ id: string; name: string }> = [];
  private _rebuildPortalScannerOptions() {
    const cust = this.selectedPortalCustomer;
    if (!cust) {
      this.portalScannerOptions = [];
      this.portalCustomerScanners = [];
      return;
    }
    const owned = this.scanners.filter(s => s.Client === cust.slug);
    this.portalScannerOptions = owned.map(s => {
      const alias = s.SsoAliases?.[cust.slug];
      return { value: alias || s.Id, label: alias ? `${alias} (${s.Id})` : s.Id };
    });
    this.portalCustomerScanners = owned
      .map(s => ({ id: s.Id, name: s.Name || s.Id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  // Whether the identity_mapping uses separated first/last fields.
  get portalUsesSeparatedNames(): boolean {
    const im = this.selectedPortalCustomer?.identity_mapping;
    return !!(im && (im.first_name || im.last_name));
  }
  get portalUsesEmailTemplate(): boolean {
    return !!this.selectedPortalCustomer?.identity_mapping?.email_template;
  }
  // v2: customer has opted into per-JWT allow_create_account control.
  get portalSupportsAllowCreateAccount(): boolean {
    return !!this.selectedPortalCustomer?.allow_create_account_enabled;
  }
  // v2: customer has a phone claim configured.
  get portalSupportsPhone(): boolean {
    return !!this.selectedPortalCustomer?.identity_mapping?.phone;
  }
  // v2 gating: identity fields (first_name, last_name, name, phone) are
  // sent only when the JWT may need to provision a user — i.e. when
  // allow_create_account is ON for a customer that supports the toggle,
  // OR when the customer doesn't have the toggle at all (legacy
  // always-send behavior).
  //
  // When allow_create_account=false (lookup-only), F2 finds the existing
  // user by email; name and phone are not needed and should not be sent.
  // Per Mike (2026-06-24): "if allow create account is false, name and
  // phone info isn't required and shouldn't be sent."
  //
  // required_claims listing first_name/last_name describes the customer's
  // CREATE-path claim contract, not what every JWT must carry. The
  // validator skips identity checks when allow_create_account=false.
  get portalRequiresIdentity(): boolean {
    return !this.portalSupportsAllowCreateAccount || this.portalAllowCreateAccount;
  }
  // Plaintext-to-base64 helper for the JWT preview. btoa is always
  // available in browsers; the unescape+encodeURIComponent dance handles
  // UTF-8 strings correctly (raw btoa rejects code points > 0xFF).
  private _b64encode(s: string): string {
    return btoa(unescape(encodeURIComponent(s)));
  }
  // Whether a given JWT claim should be base64-encoded for this customer.
  private _isBase64Field(claim: string): boolean {
    const b = this.selectedPortalCustomer?.identity_mapping?.base64_fields || [];
    return b.includes(claim);
  }

  // Paste-from-clipboard JWT decoder. Reads the clipboard, expects a
  // 3-segment JWT (header.payload.signature), decodes the middle segment,
  // and populates the identity form fields from the claims. Smart
  // base64-decode for email + phone (try the decoded value first; if it
  // looks like plain text — has @ or starts with + or is mostly digits —
  // use the decoded; otherwise leave the raw). Customer must already be
  // selected because the field-mapping depends on the customer's
  // identity_mapping schema.
  async pasteJwtAndPopulate() {
    const cust = this.selectedPortalCustomer;
    if (!cust) {
      this.toast.warning('Pick a customer first — the field-mapping depends on the customer\'s identity_mapping schema.');
      this._logEvent('skip', 'pasteJwtAndPopulate: no customer selected');
      return;
    }
    let raw = '';
    try {
      raw = await navigator.clipboard.readText();
    } catch (e: any) {
      this.toast.error('Clipboard read failed', 'Browser denied clipboard access — try copying again and clicking Paste JWT within a couple seconds.');
      this._logEvent('error', `pasteJwt: clipboard read failed: ${e?.message}`);
      return;
    }
    if (!raw) {
      this.toast.warning('Clipboard was empty — copy a JWT first');
      return;
    }
    // Accept either a bare JWT or a full URL with ?token=<jwt>.
    let jwt = raw.trim();
    const tokenMatch = jwt.match(/[?&]token=([A-Za-z0-9_\-\.]+)/);
    if (tokenMatch) jwt = tokenMatch[1];
    const parts = jwt.split('.');
    if (parts.length !== 3) {
      this.toast.error('Not a JWT', `Clipboard text doesn't look like a JWT (got ${parts.length} dot-segments, expected 3). First 60 chars: ${jwt.slice(0, 60)}`);
      this._logEvent('error', `pasteJwt: invalid segment count ${parts.length}`);
      return;
    }
    let payload: any;
    try {
      const pad = (s: string) => s + '='.repeat((4 - s.length % 4) % 4);
      const json = atob(pad(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      payload = JSON.parse(json);
    } catch (e: any) {
      this.toast.error('JWT decode failed', e?.message || 'middle segment is not valid base64url + JSON');
      this._logEvent('error', `pasteJwt: decode threw: ${e?.message}`);
      return;
    }
    if (!payload || typeof payload !== 'object') {
      this.toast.error('JWT payload not an object', `Got: ${typeof payload}`);
      return;
    }
    // Smart base64-decode helper. Returns the decoded plaintext if it
    // looks textual; otherwise returns the original string. Handles the
    // common case where the wire format is base64 but the form expects
    // plaintext.
    const smartDecode = (v: any, hint: 'email' | 'phone' | 'name'): string => {
      if (typeof v !== 'string' || !v) return '';
      try {
        const pad = (s: string) => s + '='.repeat((4 - s.length % 4) % 4);
        const decoded = atob(pad(v.replace(/-/g, '+').replace(/_/g, '/')));
        if (hint === 'email' && decoded.includes('@')) return decoded;
        if (hint === 'phone' && (/^\+?\d{7,}$/.test(decoded))) return decoded;
        if (hint === 'name' && /^[\x20-\x7e]+$/.test(decoded) && !/^[A-Za-z0-9_+/=-]+$/.test(v.slice(0, 8))) {
          // For names, only return decoded if the input doesn't look like
          // pure base64 (mixed case + symbols) — names rarely need encoding.
          return decoded;
        }
      } catch { /* not base64 — fall through */ }
      return v;
    };
    // Resolve claim keys via the customer's identity_mapping (TheoTrade,
    // MTA, OptionPit, etc. have different field names).
    const im = cust.identity_mapping || {};
    const emailKey = im.email || 'email';
    const firstKey = im.first_name || 'first_name';
    const lastKey = im.last_name || 'last_name';
    const nameKey = im.name || 'name';
    const phoneKey = im.phone || 'phone';
    let populated: string[] = [];
    // Identity fields
    if (payload[emailKey] !== undefined) {
      this.portalEmail = smartDecode(payload[emailKey], 'email');
      populated.push('email');
    } else if (im.email_template && payload[this._firstClaimKey(im.email_template)] !== undefined) {
      this.portalEmailSourceValue = String(payload[this._firstClaimKey(im.email_template)]);
      populated.push('email_source_value');
    }
    if (payload[firstKey] !== undefined) {
      this.portalFirstName = smartDecode(payload[firstKey], 'name');
      populated.push('first_name');
    }
    if (payload[lastKey] !== undefined) {
      this.portalLastName = smartDecode(payload[lastKey], 'name');
      populated.push('last_name');
    }
    if (payload[nameKey] !== undefined && !payload[firstKey] && !payload[lastKey]) {
      this.portalName = smartDecode(payload[nameKey], 'name');
      populated.push('name');
    }
    if (payload[phoneKey] !== undefined) {
      this.portalPhone = smartDecode(payload[phoneKey], 'phone');
      populated.push('phone');
    }
    // Per-JWT toggles
    if (typeof payload.allow_create_account === 'boolean') {
      this.portalAllowCreateAccount = payload.allow_create_account;
      populated.push('allow_create_account');
    }
    // Scanner — populate the slug picker if present.
    const scannerClaim = cust.scanner_selection?.scanner_claim || 'scanner';
    if (payload[scannerClaim]) {
      this.portalScannerSlug = String(payload[scannerClaim]);
      populated.push('scanner');
    }
    // exp_seconds (informational — derived from exp - iat when both present)
    if (typeof payload.exp === 'number' && typeof payload.iat === 'number') {
      const delta = payload.exp - payload.iat;
      if (delta > 0 && delta <= 86400) {
        this.portalExpSeconds = delta;
        populated.push('exp_seconds');
      }
    }
    // perms object → portalPermsJson preview
    if (payload.perms && typeof payload.perms === 'object') {
      this.portalPermsJson = JSON.stringify(payload.perms, null, 2);
      populated.push('perms');
    }
    // Any remaining claims become extras (the operator can see what wasn't
    // mapped onto a form field).
    const known = new Set([emailKey, firstKey, lastKey, nameKey, phoneKey, scannerClaim, 'allow_create_account', 'iat', 'exp', 'nbf', 'perms', 'memberships', 'type']);
    if (im.email_template) known.add(this._firstClaimKey(im.email_template));
    const extras: any = {};
    for (const [k, v] of Object.entries(payload)) {
      if (!known.has(k)) extras[k] = v;
    }
    if (Object.keys(extras).length > 0) {
      this.portalExtrasJson = JSON.stringify(extras, null, 2);
      populated.push(`extras (${Object.keys(extras).length} keys)`);
    }
    this._logEvent('action', `pasted JWT — populated: ${populated.join(', ') || '(no recognized fields)'}`);
    this.toast.success('JWT decoded', `Populated ${populated.length} field${populated.length === 1 ? '' : 's'}: ${populated.join(', ')}`);
  }

  // Multi-select selections, ordered. First = landing scanner.
  get portalSelectedScannerAliases(): string[] {
    return Object.keys(this.portalScannerAliases).filter(k => this.portalScannerAliases[k]);
  }
  // Live JWT payload preview — built from the same logic _mintAsPortal
  // uses, so what the operator sees here is exactly what the server will
  // sign. iat/exp/nbf are computed at preview time (will differ slightly
  // from the actual signed values by a second or two, but the shape is
  // accurate). Read by the template to render a small JSON box.
  get jwtPreview(): any {
    const cust = this.selectedPortalCustomer;
    if (!cust) return null;
    const im = cust.identity_mapping || {};
    const payload: any = {};
    if (im.email_template) {
      // Substitute first {claim} occurrence with the operator's source value
      payload[im.email_template ? this._firstClaimKey(im.email_template) : 'email'] = this.portalEmailSourceValue;
    } else {
      payload[im.email || 'email'] = this.portalEmail;
    }
    // v2: identity fields only when allow_create_account is on (or not configured).
    if (this.portalRequiresIdentity) {
      if (im.first_name || im.last_name) {
        payload[im.first_name || 'first_name'] = this.portalFirstName;
        payload[im.last_name || 'last_name'] = this.portalLastName;
      } else {
        payload[im.name || 'name'] = this.portalName;
      }
      if (this.portalSupportsPhone && this.portalPhone) {
        payload[im.phone || 'phone'] = this.portalPhone;
      }
    }
    if (cust.allowed_memberships.length > 0) {
      payload.memberships = this.portalSelectedMemberships;
    }
    if (cust.expected_type) payload.type = cust.expected_type;
    // Perms placement is customer-shape-aware:
    //   JWTSSO customers (Simpler): perms goes as a nested `payload.perms`
    //     object. Don't flatten — that would duplicate fields.
    //   Non-JWTSSO customers (TheoTrade): their portal sends claim values
    //     at the JWT root, so flatten the ClaimsEditor's values to top-level.
    let permsObj: Record<string, any> = {};
    try { permsObj = JSON.parse(this.portalPermsJson || '{}'); } catch { permsObj = {}; }
    if (cust.required_claims.includes('perms')) {
      payload.perms = permsObj;
    } else {
      for (const [k, v] of Object.entries(permsObj)) {
        if (payload[k] === undefined && k !== 'perms') payload[k] = v;
      }
    }
    // Top-level scanner field — present iff operator picked a scanner.
    // Backend honors payload[claimKey] in any selection mode (overrides
    // fixed scanner_id, replaces the from_jwt aliased value, etc.).
    const scannerClaimKey = cust.scanner_selection?.scanner_claim || 'scanner';
    // Skip the scanner claim entirely when the operator chose URL-mode
    // scanner input — in that mode the scanner is named in the request
    // URL (/validate-…-token/:scanner) rather than the JWT. Both modes
    // still respect portalScannerSlug as the single source of truth for
    // which scanner to land on; only the wire location differs.
    if (this.portalScannerSlug.trim() && this.portalScannerInputMode !== 'url') {
      payload[scannerClaimKey] = this.portalScannerSlug.trim();
    }
    // Legacy from_jwt multi-select scanner-aliases — preserved for
    // customers with required_claims.includes('scanners'). Single landing
    // scanner comes from portalScannerSlug above.
    if (cust.scanner_selection?.mode === 'from_jwt') {
      const selected = this.portalSelectedScannerAliases;
      if (selected.length > 0 && cust.required_claims.includes('scanners')) {
        payload.scanners = selected;
      }
    }
    // v2: allow_create_account is a fixed-name payload setting.
    if (this.portalSupportsAllowCreateAccount) {
      payload.allow_create_account = this.portalAllowCreateAccount;
    }
    // Extras
    try {
      const extras = JSON.parse(this.portalExtrasJson || '{}');
      if (extras && typeof extras === 'object') Object.assign(payload, extras);
    } catch {}
    const now = Math.floor(Date.now() / 1000);
    payload.iat = now;
    // v2: include exp only when the schema asks for it (JWTSSO-shape) — minimal
    // payloads (TheoTrade v2) sign with iat only and rely on max_iat_age_seconds.
    if (cust.required_claims.includes('exp') || cust.expected_type === 'JWTSSO') {
      payload.exp = now + Math.max(5, Math.min(300, this.portalExpSeconds || 60));
    }
    if (cust.required_claims.includes('nbf')) payload.nbf = now;
    // v2: base64-encode configured fields LAST so the preview matches the wire.
    const b64 = im.base64_fields || [];
    for (const fname of b64) {
      const v = payload[fname];
      if (typeof v === 'string' && v.length > 0) payload[fname] = this._b64encode(v);
    }
    return payload;
  }
  get jwtPreviewJson(): string {
    const p = this.jwtPreview;
    return p ? JSON.stringify(p, null, 2) : '';
  }
  private _firstClaimKey(tmpl: string): string {
    const m = tmpl.match(/\{([a-zA-Z0-9_]+)\}/);
    return m ? m[1] : 'email';
  }

  // ----- Claims editor popup (embedded ClaimsEditorComponent) -----
  openPermsPopup() { this.claimsEditorOpen = true; }
  closePermsPopup() { this.claimsEditorOpen = false; }
  // ClaimsEditor emits the canonical JSON array of per-scanner entries.
  // For the tester we take the FIRST entry's non-scanner fields and ship
  // them as JWT top-level claims (matches legacy TheoTrade/Simpler shape).
  onClaimsEditorSave(json: string) {
    this.claimsEditorJson = json;
    this.portalPermsJson = JSON.stringify(this._extractPermsFromClaimsJson(json), null, 2);
    this.claimsEditorOpen = false;
  }
  // Translate the editor's [{scanner, ...fields}] array into the perms-
  // shaped object the rest of the tester already expects. The `scanner`
  // field is dropped here — scanner selection is driven by the alias
  // dropdown (for from_jwt) or the fixed scanner_id elsewhere. All other
  // schema fields pass through as flat key/value perms.
  private _extractPermsFromClaimsJson(json: string): Record<string, any> {
    try {
      const arr = JSON.parse(json || '[]');
      if (!Array.isArray(arr) || arr.length === 0) return {};
      const first = arr[0] || {};
      const out: Record<string, any> = {};
      for (const [k, v] of Object.entries(first)) {
        if (k === 'scanner') continue;
        out[k] = v;
      }
      return out;
    } catch { return {}; }
  }
  // Rebuild claimsEditorCustomers (called when the selected portal
  // customer changes — see onPortalCustomerChange). Stable reference
  // keeps ClaimsEditor's ngOnChanges from firing on every CD tick.
  private _rebuildClaimsEditorCustomers() {
    const cust = this.selectedPortalCustomer;
    this.claimsEditorCustomers = cust ? [{
      _id: cust.slug,
      name: cust.name,
      scanner_ids: cust.scanner_ids || [],
      claims_schema: cust.claims_schema,
    }] : [];
  }

  // Effective list of selected membership slugs for the JWT payload.
  get portalSelectedMemberships(): string[] {
    return Object.keys(this.portalMemberships).filter(k => this.portalMemberships[k]);
  }

  // Scanner row matching the selected portal customer's sso_portal.scanner_id.
  // Used to surface the F2-ADMIN.Scanners.Url that drives the redirect — so the
  // operator can see exactly which launch URL the validate call will return,
  // before clicking Sign + Validate. {tld} placeholders are substituted with
  // the current host's TLD (matches what the backend does at request time).
  get portalScannerRow(): ScannerRow | undefined {
    const cust = this.selectedPortalCustomer;
    if (!cust) return undefined;
    return this.scanners.find(s => s.Id === cust.scanner_id);
  }

  // Effective launch URL preview — applies {tld} substitution + optional
  // Customers.scanner_host override. Mirrors auth.service._get_scanner_redirect_url
  // so the operator can verify the host/path BEFORE signing the JWT.
  get portalExpectedRedirect(): string {
    const cust = this.selectedPortalCustomer;
    const row = this.portalScannerRow;
    if (!cust || !row?.Url) return '';
    const tld = (typeof window !== 'undefined' && window.location.hostname.endsWith('.com')) ? 'com' : 'ai';
    let urlStr = String(row.Url).replace(/\{tld\}/g, tld);
    try {
      const parsed = new URL(urlStr);
      if (cust.scanner_host) parsed.host = cust.scanner_host;
      return `${parsed.origin}${parsed.pathname}?sid=…`;
    } catch {
      return urlStr;
    }
  }

  // True when Step 3 (mint JWT + validate) has everything it needs. The
  // required fields depend on the customer's identity_mapping + selection
  // mode, not a fixed list.
  get portalReady(): boolean {
    const cust = this.selectedPortalCustomer;
    if (!cust) return false;
    // Email is always required (or its source value for email_template).
    if (this.portalUsesEmailTemplate) {
      if (!this.portalEmailSourceValue) return false;
    } else if (!this.portalEmail) {
      return false;
    }
    // v2: identity fields (names, phone) only required when allow_create_account
    // is off OR not configured (lookup-only mode skips them).
    if (this.portalRequiresIdentity) {
      if (this.portalUsesSeparatedNames) {
        if (!this.portalFirstName || !this.portalLastName) return false;
      } else if (!this.portalName) {
        return false;
      }
    }
    // Memberships only required when the customer's schema enforces them.
    if (cust.allowed_memberships.length > 0 && this.portalSelectedMemberships.length === 0) return false;
    // Scanner selection — v2 minimal payloads can omit; only required for from_jwt
    // mode when the customer has NO fixed scanner_id fallback.
    if (cust.scanner_selection?.mode === 'from_jwt' && this.portalSelectedScannerAliases.length === 0 && !cust.scanner_id) {
      return false;
    }
    return true;
  }

  // Dispatcher — picks the admin-cookie / customer-token / portal / mta
  // path based on authPath. All branches write to mintReq/mintRes so the
  // rest of the page (display, iframe, redeem-also-test) stays path-agnostic.
  async mintSid() {
    const intent = this.isPortalPath
      ? (this.portalDryRun ? `${this.authPath} dry-run validate` : `${this.authPath} REAL grant`)
      : `${this.authPath} mint`;
    this._logEvent('click', `mintSid → ${intent}`);
    if (this.isPortalPath) {
      if (!this.portalReady) { this._logEvent('skip', 'mintSid skipped: portalReady=false'); return; }
      await this._mintAsPortal();
    } else if (this.isMtaPath) {
      if (!this.mtaReady) { this._logEvent('skip', 'mintSid skipped: mtaReady=false'); return; }
      await this._mintAsMta();
    } else {
      if (!this.selectedScannerId) { this._logEvent('skip', 'mintSid skipped: no scanner selected'); return; }
      if (this.authPath === 'customer') {
        if (!this.custBundle?.id_token) { this._logEvent('skip', 'mintSid skipped: customer not logged in'); return; }
        await this._mintAsMember();
      } else {
        await this._mintAsAdmin();
      }
    }
    this._logEvent('response', `mintRes ${this.mintRes?.status ?? '—'} ok=${this.mintRes?.ok} dur=${this.mintRes?.durationMs}ms`, this.mintRes?.body?.err || this.mintRes?.body?.code);
    // Reset SID lifecycle for admin + customer paths too (portal sets its
    // own in _mintAsPortal using the per-customer ttl; MTA doesn't return
    // a launch URL so the sid-lifecycle timer stays null). Use a 90s
    // default here since those paths don't carry sid_ttl_seconds through.
    if (!this.isPortalPath && !this.isMtaPath && this.mintRes?.ok && this.mintRes.body?.url) {
      this.sidExpiresAt = Date.now() + 90 * 1000;
      this.sidConsumed = false;
    }
    // Optional redeem-side test.
    if (this.mintRes?.body?.url && this.redeemAlsoTest) {
      const sid = this._extractSid(this.mintRes.body.url);
      if (sid) await this.redeemSid(sid);
    }
  }

  // Portal-path: two server-to-server calls in sequence —
  //   3a. POST /admin/customer-directory/sso-portal/sign-test-token
  //       → server signs an HS256 JWT using the customer's secret env var
  //       and returns it. Secret never leaves the server.
  //   3b. POST /auth/validate-portal-token/:customer { token }
  //       → server validates the JWT against the same customer's sso_portal
  //       config (the production path), mints a SID, returns the redirect URL.
  //
  // The validate response shape is { valid, redirect } (PDF Section 6).
  // Normalize body.redirect → body.url so the existing Step 4 / Step 5
  // open-tab / embed-iframe / parsedUrl code can read the URL without
  // path-specific branches.
  private async _mintAsPortal() {
    const cust = this.selectedPortalCustomer;
    if (!cust) return;
    // Perms come from the ClaimsEditor popup's extracted field values
    // (already a flat key/value object). Used in two ways:
    //   1. As the JWT's `perms` object for JWTSSO-shaped customers
    //   2. Merged as TOP-LEVEL claims for legacy-shape customers
    //      (TheoTrade's live_data, MTA's access_tier, etc. live at root)
    let perms: any = {};
    try { perms = JSON.parse(this.portalPermsJson || '{}'); } catch { perms = {}; }

    // ---- Step 3a: sign the JWT server-side ----
    this.signing_token = true;
    this.portalSignedToken = '';
    // Build claims dict — only the customer-shape-relevant fields. The
    // server's sign-test-token translates these into the JWT shape per
    // identity_mapping/required_claims (no guessing here).
    const claims: any = {
      exp_seconds: this.portalExpSeconds,
      perms,
    };
    if (this.portalUsesEmailTemplate) {
      // The email is computed server-side via template; we just send the
      // source field's value (e.g. user_identifier='abc123').
      if (this.portalEmailSourceClaim) claims[this.portalEmailSourceClaim] = this.portalEmailSourceValue;
    } else {
      claims.email = this.portalEmail;
    }
    // v2: identity fields (and phone) only when allow_create_account is on
    // or not configured. Lookup-only mode (checkbox OFF) sends ONLY email.
    if (this.portalRequiresIdentity) {
      if (this.portalUsesSeparatedNames) {
        claims.first_name = this.portalFirstName;
        claims.last_name = this.portalLastName;
      } else {
        claims.name = this.portalName;
      }
      if (this.portalSupportsPhone && this.portalPhone) {
        claims.phone = this.portalPhone;
      }
    }
    if (cust.allowed_memberships.length > 0) {
      claims.memberships = this.portalSelectedMemberships;
    }
    // v2: per-JWT allow_create_account toggle. Fixed claim name per spec.
    if (this.portalSupportsAllowCreateAccount) {
      claims.allow_create_account = this.portalAllowCreateAccount;
    }
    // Scanner field — single source of truth is portalScannerSlug
    // (operator-chosen via the picker, applies to any selection mode).
    // Suppressed when scannerInputMode='url' — in URL mode the scanner
    // lives in the request path, not the JWT — so the preview matches
    // what the backend will actually receive.
    const scannerClaim = cust.scanner_selection?.scanner_claim || 'scanner';
    if (this.portalScannerSlug.trim() && this.portalScannerInputMode !== 'url') {
      claims[scannerClaim] = this.portalScannerSlug.trim();
    }
    // Legacy multi-scanner aliases — only when the customer's schema
    // declares a 'scanners' entitlement array (Simpler-style).
    if (cust.scanner_selection?.mode === 'from_jwt'
        && this.portalSelectedScannerAliases.length > 0
        && cust.required_claims.includes('scanners')) {
      claims.scanners = this.portalSelectedScannerAliases;
    }
    // ClaimsEditor-derived perms also flatten into top-level claims so
    // legacy-shape customers (TheoTrade with live_data; MTA with
    // access_tier) get them as JWT root claims, not nested under perms.
    // JWTSSO customers (Simpler) also get them under perms via the
    // separate `perms` field assignment above.
    for (const [k, v] of Object.entries(perms)) {
      if (claims[k] === undefined) claims[k] = v;
    }
    // Generic extras textarea (operator-supplied overrides) merged last.
    try {
      const extras = JSON.parse(this.portalExtrasJson || '{}');
      if (extras && typeof extras === 'object') Object.assign(claims, extras);
    } catch { /* ignore — operator will see the validate error */ }

    this.signTokenReq = {
      url: `${this.apiBase}/admin/customer-directory/sso-portal/sign-test-token`,
      method: 'POST',
      // secret_id, when set, picks WHICH of the customer's stored secrets
      // to sign with (lets the operator exercise rotation overlap or test
      // a newly-imported portal-side secret). Empty → backend defaults to
      // the newest (last-pushed) secret.
      body: { customer: cust.slug, claims, secret_id: this.portalSigningSecretId || undefined },
      withCredentials: true,
    };
    this.signTokenRes = null;
    let t0 = performance.now();
    try {
      const res = await this.http.post(this.signTokenReq.url, this.signTokenReq.body, {
        withCredentials: true,
        observe: 'response',
      }).toPromise() as HttpResponse<any>;
      this.signTokenRes = {
        status: res.status,
        ok: res.ok && !!res.body?.token,
        body: res.body,
        durationMs: Math.round(performance.now() - t0),
      };
      this.portalSignedToken = res.body?.token || '';
    } catch (e: any) {
      this.signTokenRes = {
        status: e?.status ?? null,
        ok: false,
        body: e?.error,
        error: e?.message || 'sign-test-token failed',
        durationMs: Math.round(performance.now() - t0),
      };
    } finally {
      this.signing_token = false;
    }
    if (!this.portalSignedToken) {
      this.mintReq = null;
      this.mintRes = null;
      this.exchangeReq = null;
      this.exchangeRes = null;
      return;
    }

    // ---- Step 3b: validate the JWT (production validate-* path) ----
    // dry_run gate: when ON (default), backend skips ALL Cognito writes
    // and returns the would-be diff. When OFF, real production grant
    // happens — requires explicit operator confirmation before _mintAsPortal
    // is called (see _ensureGrantConfirmed).
    //
    // Endpoint selection per authPath:
    //   'portal-sid'   → /validate-portal-token/:customer (force_sid_redirect
    //                    server-side → ALWAYS ?sid= URL, regardless of
    //                    customer's handoff_mode)
    //   'portal-token' → /validate-customer-token { customer, token } (honors
    //                    handoff_mode → ?token= URL for token_query customers)
    //
    // The 'portal-token' radio is disabled in step 2 when the customer
    // doesn't have handoff_mode='token_query', so the operator can't fall
    // into the token path on a sid-only customer.
    this.minting = true;
    const useSidPath = this.authPath === 'portal-sid';
    // Resolve the base URL. When the operator chose "use customer host" AND
    // the customer has a scanner_host, POST goes to
    // https://<scanner_host>/rest — matching the URL shape production
    // traffic uses. Otherwise falls back to the admin API base
    // (env.apiUrl/rest). This is the 2026-07-08 tester feature so a
    // customer-host routing misconfig surfaces in the tester before it
    // hits a Laravel handler at 3am.
    const resolvedApiBase = (this.usePortalCustomerHost && cust.scanner_host)
      ? `https://${cust.scanner_host}/rest`
      : this.apiBase;
    // Resolve the URL path slug. Default ('jwt' scanner mode) keeps the
    // customer slug in the URL and puts the scanner in the JWT payload.
    // 'url' mode puts a scanner id in the URL path via the /:slug overload
    // on validate-{portal,customer}-token (backend 4aabba3 + c4cc8d1); if
    // the operator hasn't picked a scanner (portalScannerSlug is empty),
    // fall back to the customer slug so the URL still resolves.
    const urlSlug = (this.portalScannerInputMode === 'url' && this.portalScannerSlug.trim())
      ? this.portalScannerSlug.trim()
      : cust.slug;
    // IT-F2-437 cid:6718e44b: omit the `/<slug>` suffix when the operator
    // chose "omit slug" AND we're on the branded host (otherwise the Host
    // header would be admin.f2-tech.ai, which the backend can't resolve
    // to a customer). On the token path the omit toggle also applies —
    // backend already honored a Host-derived customer there since the
    // byte-exact host-match ask.
    const omitSlug = this.omitPortalSlug && this.usePortalCustomerHost && !!cust.scanner_host;
    const mintUrl = useSidPath
      ? (omitSlug
          ? `${resolvedApiBase}/auth/validate-portal-token`
          : `${resolvedApiBase}/auth/validate-portal-token/${encodeURIComponent(urlSlug)}`)
      : (this.portalScannerInputMode === 'url' && this.portalScannerSlug.trim()
          ? `${resolvedApiBase}/auth/validate-customer-token/${encodeURIComponent(urlSlug)}`
          : `${resolvedApiBase}/auth/validate-customer-token`);
    const mintBody: any = useSidPath
      ? { token: this.portalSignedToken, dry_run: this.portalDryRun }
      : { customer: cust.slug, token: this.portalSignedToken, dry_run: this.portalDryRun };
    const mintBodyDisplay: any = useSidPath
      ? {
          token: `${this.portalSignedToken.slice(0, 32)}… (${this.portalSignedToken.length} chars)`,
          dry_run: this.portalDryRun,
        }
      : {
          customer: cust.slug,
          token: `${this.portalSignedToken.slice(0, 32)}… (${this.portalSignedToken.length} chars)`,
          dry_run: this.portalDryRun,
        };
    this.mintReq = {
      url: mintUrl,
      method: 'POST',
      body: mintBodyDisplay,
      bodyRaw: mintBody,        // wire-shape body — full untruncated tokens for copyCurl
      withCredentials: false,
    };
    this.mintRes = null;
    t0 = performance.now();
    try {
      const res = await this.http.post(mintUrl, mintBody, {
        observe: 'response',
      }).toPromise() as HttpResponse<any>;
      // Normalize: validate-portal-token returns { valid, redirect } per the
      // PDF; downstream Step 4/5 expects body.url. Copy across so existing
      // open-tab / iframe / parsedUrl code works unchanged.
      const body = res.body || {};
      if (body.redirect && !body.url) body.url = body.redirect;
      this.mintRes = {
        status: res.status,
        ok: res.ok && body?.valid === true,
        body,
        durationMs: Math.round(performance.now() - t0),
      };
      // Reset URL-lifecycle for the open/embed buttons. TTL source depends
      // on the handoff mode:
      //   sid mode: customer's sso_portal.sid_ttl_seconds (default 90)
      //   token_query mode: decode the outbound JWT's exp claim if present.
      //                     The identity-shape JWT (TheoTrade v2 minimal)
      //                     omits exp by design — symmetric with the inbound
      //                     portal contract that relies on iat freshness
      //                     instead. Fall back to a 60s URL-display window
      //                     so the operator sees a finite countdown.
      // When the timer expires OR either button is clicked, both disable
      // until next mint.
      if (this.mintRes.ok && body?.url) {
        const mode = this.returnedHandoffMode;
        if (mode === 'token_query') {
          const payload = this.outboundJwtPayload;
          if (payload?.exp && typeof payload.exp === 'number') {
            this.sidExpiresAt = payload.exp * 1000;
          } else {
            // Defensive fallback if the JWT doesn't carry an exp.
            this.sidExpiresAt = Date.now() + 60 * 1000;
          }
        } else {
          const ttl = this.selectedPortalCustomer?.sid_ttl_seconds || 90;
          this.sidExpiresAt = Date.now() + ttl * 1000;
        }
        this.sidConsumed = false;
      }
      // NB: previously snapped portalDryRun back to true after a successful
      // real grant for extra safety. That broke the legitimate "real grant
      // → Embed Iframe" flow, because embedInIframe() re-calls mintSid()
      // to get a fresh single-use sid; the snap-back caused that re-call
      // to go out as dry-run and return the diff instead of a usable URL.
      // Safety is now enforced solely by the toggle's confirm() modal —
      // the operator has already named the customer + email when going
      // off dry-run. Resetting on customer-change keeps fresh selections
      // defaulted safe (see onPortalCustomerChange).
    } catch (e: any) {
      this.mintRes = {
        status: e?.status ?? null,
        ok: false,
        body: e?.error,
        error: e?.message || (useSidPath ? 'validate-portal-token failed' : 'validate-customer-token failed'),
        durationMs: Math.round(performance.now() - t0),
      };
    } finally {
      this.minting = false;
    }
  }

  // MTA-endpoint path: two server-to-server calls in sequence, mirroring
  // the shape of _mintAsPortal so the rest of the page is path-agnostic.
  //   1. POST /rest/admin/customer-directory/sign-mta-test-jwt
  //        → server signs an HS256 JWT with env.MTA_JWT_SECRET_KEY.
  //          Returns { jwt, payload, fingerprint }. Secret never leaves
  //          the server; fingerprint is sha256(secret) first-12 for
  //          operator confirmation of which key was used.
  //   2. POST /rest/auth/validate-mta-token { token }
  //        → the production endpoint. Backend resolves the JWT's
  //          `scanner` claim against F2-ADMIN.Scanners to find the
  //          owning customer, checks Customers.<slug>.
  //          mta_endpoint_allowed (or _id==='mta' implicit), and
  //          returns the validated identity bundle.
  //
  // Unlike the portal path this endpoint does NOT return a launch URL —
  // it's the last-mile validator, not a mint. Step 4 (Open Tab / Embed
  // Iframe) stays disabled on this path.
  private async _mintAsMta() {
    // ---- Step 1: sign the JWT server-side ----
    this.signing_mta_jwt = true;
    this.mtaSignedJwt = '';
    this.mtaSignedPayload = null;
    this.mtaSignFingerprint = '';
    const signPayload: any = {
      user_identifier: this.mtaUserIdentifier,
      access_tier: this.mtaAccessTier,
      scanner: this.mtaScannerClaim.trim(),
    };
    this.mtaSignReq = {
      url: `${this.apiBase}/admin/customer-directory/sign-mta-test-jwt`,
      method: 'POST',
      body: { payload: signPayload, expires_in_seconds: this.mtaExpSeconds },
      withCredentials: true,
    };
    this.mtaSignRes = null;
    let t0 = performance.now();
    try {
      const res = await this.http.post(this.mtaSignReq.url, this.mtaSignReq.body, {
        withCredentials: true,
        observe: 'response',
      }).toPromise() as HttpResponse<any>;
      this.mtaSignRes = {
        status: res.status,
        ok: res.ok && !!res.body?.jwt,
        body: res.body,
        durationMs: Math.round(performance.now() - t0),
      };
      this.mtaSignedJwt = res.body?.jwt || '';
      this.mtaSignedPayload = res.body?.payload || null;
      this.mtaSignFingerprint = res.body?.fingerprint || '';
    } catch (e: any) {
      this.mtaSignRes = {
        status: e?.status ?? null,
        ok: false,
        body: e?.error,
        error: e?.message || 'sign-mta-test-jwt failed',
        durationMs: Math.round(performance.now() - t0),
      };
    } finally {
      this.signing_mta_jwt = false;
    }
    if (!this.mtaSignedJwt) {
      this.mintReq = null;
      this.mintRes = null;
      return;
    }

    // ---- Step 2: POST the signed JWT to the production validate endpoint ----
    this.minting = true;
    const validateUrl = `${this.apiBase}/auth/validate-mta-token`;
    const validateBody: any = { token: this.mtaSignedJwt };
    const validateBodyDisplay: any = {
      token: `${this.mtaSignedJwt.slice(0, 32)}… (${this.mtaSignedJwt.length} chars)`,
    };
    this.mintReq = {
      url: validateUrl,
      method: 'POST',
      body: validateBodyDisplay,
      bodyRaw: validateBody,
      withCredentials: false,
    };
    this.mintRes = null;
    t0 = performance.now();
    try {
      const res = await this.http.post(validateUrl, validateBody, {
        observe: 'response',
      }).toPromise() as HttpResponse<any>;
      this.mintRes = {
        status: res.status,
        ok: res.ok,
        body: res.body,
        durationMs: Math.round(performance.now() - t0),
      };
    } catch (e: any) {
      this.mintRes = {
        status: e?.status ?? null,
        ok: false,
        body: e?.error,
        error: e?.message || 'validate-mta-token failed',
        durationMs: Math.round(performance.now() - t0),
      };
    } finally {
      this.minting = false;
    }
  }

  // Standalone Sign action for the MTA form — same server call as the
  // first half of _mintAsMta, but doesn't chain into validate. Lets the
  // operator confirm the signed JWT looks right before firing it at the
  // production validator. Bound to the "Sign test JWT" button.
  async signMtaTestJwt(): Promise<void> {
    if (!this.mtaReady) {
      this._logEvent('skip', 'signMtaTestJwt: mtaReady=false');
      return;
    }
    this._logEvent('click', 'signMtaTestJwt (standalone, no validate)');
    this.signing_mta_jwt = true;
    this.mtaSignedJwt = '';
    this.mtaSignedPayload = null;
    this.mtaSignFingerprint = '';
    const signPayload: any = {
      user_identifier: this.mtaUserIdentifier,
      access_tier: this.mtaAccessTier,
      scanner: this.mtaScannerClaim.trim(),
    };
    this.mtaSignReq = {
      url: `${this.apiBase}/admin/customer-directory/sign-mta-test-jwt`,
      method: 'POST',
      body: { payload: signPayload, expires_in_seconds: this.mtaExpSeconds },
      withCredentials: true,
    };
    this.mtaSignRes = null;
    const t0 = performance.now();
    try {
      const res = await this.http.post(this.mtaSignReq.url, this.mtaSignReq.body, {
        withCredentials: true,
        observe: 'response',
      }).toPromise() as HttpResponse<any>;
      this.mtaSignRes = {
        status: res.status,
        ok: res.ok && !!res.body?.jwt,
        body: res.body,
        durationMs: Math.round(performance.now() - t0),
      };
      this.mtaSignedJwt = res.body?.jwt || '';
      this.mtaSignedPayload = res.body?.payload || null;
      this.mtaSignFingerprint = res.body?.fingerprint || '';
    } catch (e: any) {
      this.mtaSignRes = {
        status: e?.status ?? null,
        ok: false,
        body: e?.error,
        error: e?.message || 'sign-mta-test-jwt failed',
        durationMs: Math.round(performance.now() - t0),
      };
    } finally {
      this.signing_mta_jwt = false;
    }
    this._logEvent('response', `mtaSignRes ${this.mtaSignRes?.status ?? '—'} ok=${this.mtaSignRes?.ok}`);
  }

  // Standalone Validate action — POSTs the already-signed JWT to the
  // production validate-mta-token endpoint. Bound to the "Validate" button
  // in the MTA form. Runs independently of Sign so the operator can retry
  // validate against a stored JWT without re-signing (matters when
  // testing iat-freshness edge cases).
  async validateMtaToken(): Promise<void> {
    if (!this.mtaSignedJwt) {
      this._logEvent('skip', 'validateMtaToken: no signed JWT');
      return;
    }
    this._logEvent('click', 'validateMtaToken (standalone POST)');
    this.minting = true;
    const validateUrl = `${this.apiBase}/auth/validate-mta-token`;
    const validateBody: any = { token: this.mtaSignedJwt };
    const validateBodyDisplay: any = {
      token: `${this.mtaSignedJwt.slice(0, 32)}… (${this.mtaSignedJwt.length} chars)`,
    };
    this.mintReq = {
      url: validateUrl,
      method: 'POST',
      body: validateBodyDisplay,
      bodyRaw: validateBody,
      withCredentials: false,
    };
    this.mintRes = null;
    const t0 = performance.now();
    try {
      const res = await this.http.post(validateUrl, validateBody, {
        observe: 'response',
      }).toPromise() as HttpResponse<any>;
      this.mintRes = {
        status: res.status,
        ok: res.ok,
        body: res.body,
        durationMs: Math.round(performance.now() - t0),
      };
    } catch (e: any) {
      this.mintRes = {
        status: e?.status ?? null,
        ok: false,
        body: e?.error,
        error: e?.message || 'validate-mta-token failed',
        durationMs: Math.round(performance.now() - t0),
      };
    } finally {
      this.minting = false;
    }
    this._logEvent('response', `mintRes ${this.mintRes?.status ?? '—'} ok=${this.mintRes?.ok}`);
  }

  // POST /rest/auth/mint-scanner-sid-cookie {scannerId}
  // Cookie-authed — the admin's f2_id cookie carries identity.
  private async _mintAsAdmin() {
    this.minting = true;
    this.mintReq = {
      url: `${this.apiBase}/auth/mint-scanner-sid-cookie`,
      method: 'POST',
      body: { scannerId: this.selectedScannerId },
      withCredentials: true,
    };
    this.mintRes = null;
    const t0 = performance.now();
    try {
      const res = await this.http.post(this.mintReq.url, this.mintReq.body, {
        withCredentials: true,
        observe: 'response',
      }).toPromise() as HttpResponse<any>;
      this.mintRes = {
        status: res.status,
        ok: res.ok,
        body: res.body,
        durationMs: Math.round(performance.now() - t0),
      };
    } catch (e: any) {
      this.mintRes = {
        status: e?.status ?? null,
        ok: false,
        body: e?.error,
        error: e?.message || 'request failed',
        durationMs: Math.round(performance.now() - t0),
      };
    } finally {
      this.minting = false;
    }
  }

  // POST /rest/auth/mint-scanner-sid {token, scannerId, accessToken,
  // idToken, refreshToken} — token-authed path the f2-members React
  // SPA uses on tile click. Carries the FULL bundle so the per-product
  // SPA's _redeem_sid sees access_token + id_token + refresh_token
  // (the fix from f2-admin-service #66 / f2-members #37).
  private async _mintAsMember() {
    this.minting = true;
    this.mintReq = {
      url: `${this.apiBase}/auth/mint-scanner-sid`,
      method: 'POST',
      body: {
        token:        this.custBundle.id_token,  // legacy id-token field
        scannerId:    this.selectedScannerId,
        accessToken:  this.custBundle.access_token || '',
        idToken:      this.custBundle.id_token,
        refreshToken: this.custBundle.refresh_token || '',
      },
      withCredentials: false,
    };
    this.mintRes = null;
    const t0 = performance.now();
    try {
      const res = await this.http.post(this.mintReq.url, this.mintReq.body, {
        observe: 'response',
      }).toPromise() as HttpResponse<any>;
      this.mintRes = {
        status: res.status,
        ok: res.ok,
        body: res.body,
        durationMs: Math.round(performance.now() - t0),
      };
    } catch (e: any) {
      this.mintRes = {
        status: e?.status ?? null,
        ok: false,
        body: e?.error,
        error: e?.message || 'request failed',
        durationMs: Math.round(performance.now() - t0),
      };
    } finally {
      this.minting = false;
    }
  }

  // POST /rest/auth/redeem-session {sid}
  async redeemSid(sid: string) {
    if (!sid) return;
    this.redeeming = true;
    this.redeemReq = {
      url: `${this.apiBase}/auth/redeem-session`,
      method: 'POST',
      body: { sid },
      withCredentials: false,
    };
    this.redeemRes = null;
    const t0 = performance.now();
    try {
      const res = await this.http.post(this.redeemReq.url, this.redeemReq.body, {
        observe: 'response',
      }).toPromise() as HttpResponse<any>;
      this.redeemRes = {
        status: res.status,
        ok: res.ok,
        body: res.body,
        durationMs: Math.round(performance.now() - t0),
      };
      // Reflect backend single-use vs grace behavior in the Tester state,
      // same as the openInNewTab / embedInIframe / exchangeToken paths.
      if (res.ok) this._applyConsumedOrGrace();
    } catch (e: any) {
      this.redeemRes = {
        status: e?.status ?? null,
        ok: false,
        body: e?.error,
        error: e?.message || 'request failed',
        durationMs: Math.round(performance.now() - t0),
      };
    } finally {
      this.redeeming = false;
    }
  }

  // POST /rest/auth/exchange-portal-token {customer, token} — token-mode
  // round-trip. Simulates the customer SPA's landing behavior: strips the
  // identity JWT from `?token=` and POSTs it back to get a `?sid=` URL
  // it then navigates to. Response shape is { valid, url } where url is
  // the same scanner URL but with `?sid=<sid>` so the standard SID
  // redemption flow takes over from there.
  //
  // This proves the full chain works: F2 signature on the outbound JWT
  // round-trips through verify + find/create + login + mintSid, the
  // resulting sid is fresh + redeemable. The mint URL the operator was
  // working with stays alive — the exchange does NOT consume the original
  // token (validate_customer_token is idempotent against iat-fresh JWTs
  // with the same identity).
  async exchangeToken() {
    const url = this.mintRes?.body?.url;
    const cust = this.selectedPortalCustomer;
    if (!url || !cust) return;
    // The mint response's URL carries the JWT in the `?token=` query — same
    // string the consumer SPA would read off window.location.
    const m = url.match(/[?&]token=([^&]+)/);
    if (!m) {
      this._logEvent('error', 'exchangeToken: no ?token= in mint URL');
      return;
    }
    const token = decodeURIComponent(m[1]);
    this.exchanging = true;
    // Send the scanner the operator picked (portalScannerSlug) as the
    // "endpoint consuming the token" signal. F2 uses it for entitlement
    // checks + builds the returned ?sid= URL to point back at the same
    // scanner path. Empty = members-page emission (no scanner-specific
    // path).
    const exchangeBody: any = { customer: cust.slug, token };
    if (this.portalScannerSlug.trim()) {
      exchangeBody.scanner = this.portalScannerSlug.trim();
    }
    this.exchangeReq = {
      url: `${this.apiBase}/auth/exchange-portal-token`,
      method: 'POST',
      body: exchangeBody,
      withCredentials: false,
    };
    this.exchangeRes = null;
    const t0 = performance.now();
    try {
      const res = await this.http.post(this.exchangeReq.url, this.exchangeReq.body, {
        observe: 'response',
      }).toPromise() as HttpResponse<any>;
      this.exchangeRes = {
        status: res.status,
        ok: res.ok,
        body: res.body,
        durationMs: Math.round(performance.now() - t0),
      };
    } catch (e: any) {
      this.exchangeRes = {
        status: e?.status ?? null,
        ok: false,
        body: e?.error,
        error: e?.message || 'request failed',
        durationMs: Math.round(performance.now() - t0),
      };
    } finally {
      this.exchanging = false;
    }
    this._logEvent('response', `exchangeRes ${this.exchangeRes?.status ?? '—'} ok=${this.exchangeRes?.ok} dur=${this.exchangeRes?.durationMs}ms`);
  }

  // Mint a fresh sid + load in iframe (always re-mints — sids are single-use,
  // so the previous one is dead after redeem-also-test or after a prior embed).
  async embedInIframe() {
    this._logEvent('click', 'embedInIframe → re-mint + load iframe');
    await this.mintSid();
    const url = this.mintRes?.body?.url;
    if (url) {
      this.iframeSrc = url;
      this.iframeSafeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      this.iframeKey++;
      this._logEvent('iframe', `loaded src=${url.slice(0, 120)}${url.length > 120 ? '…' : ''}`);
      // Single-use sid is consumed by the iframe's redeem — UNLESS the
      // customer has a grace window, in which case the SID stays alive
      // for grace_seconds. See _applyConsumedOrGrace below.
      this._applyConsumedOrGrace();
      this._postGrantUseRevertToDryRun();
    } else {
      this._logEvent('skip', 'embedInIframe: no body.url in mintRes (dry-run? or grant failed)');
    }
  }

  closeIframe() {
    this.iframeSrc = null;
    this.iframeSafeSrc = null;
  }

  openInNewTab() {
    const url = this.mintRes?.body?.url;
    this._logEvent('click', `openInNewTab url=${url ? url.slice(0, 80) : '(none)'}`);
    if (url) {
      window.open(url, '_blank', 'noopener');
      // Single-use sid — consumed by the destination on first read.
      // With a configured grace window, the SID survives for grace seconds.
      this._applyConsumedOrGrace();
      this._postGrantUseRevertToDryRun();
    }
  }

  // Shared post-redeem state transition. Called from openInNewTab,
  // embedInIframe, exchangeToken, and the redeem-also-test path. Honors
  // the customer's sid_redeem_grace_seconds so the Tester accurately
  // mirrors the backend's peek+commit behavior:
  //   grace === 0 → SID is GETDEL'd backend-side → flip sidConsumed (locks UI)
  //   grace > 0   → SID's TTL was shortened backend-side → keep sidConsumed
  //                  false but reset countdown to grace_seconds so the
  //                  operator can see how long until it truly dies
  private _applyConsumedOrGrace() {
    const cust = this.selectedPortalCustomer;
    const grace = Math.max(0, Math.floor(cust?.sid_redeem_grace_seconds || 0));
    if (this.isPortalPath && grace > 0 && this.returnedHandoffMode === 'sid') {
      // sid-mode + grace configured: backend EXPIRE-LT'd the key. The new
      // TTL is min(currentTTL, grace). Reflect the shorter window in the
      // countdown — but never extend (LT semantics) past whatever was
      // already showing.
      const newExpires = Date.now() + grace * 1000;
      if (this.sidExpiresAt == null || newExpires < this.sidExpiresAt) {
        this.sidExpiresAt = newExpires;
      }
      this.sidConsumed = false;
      return;
    }
    if (this.isPortalPath && grace > 0 && this.returnedHandoffMode === 'token_query') {
      // token-mode + grace configured: same backend semantics on the
      // underlying SID. JWT exp is independent and may still be much
      // longer — the countdown driven by JWT exp stays accurate as the
      // ceiling. Don't flip sidConsumed.
      return;
    }
    // Default: strict single-use — flip the consumed flag, lock the buttons.
    this.sidConsumed = true;
  }

  // ----- Customer-side credentials flow -----

  // POST /rest/auth/login {email, password} — the same call f2-members
  // makes on its self-hosted login form. Backend runs InitiateAuth
  // against Cognito and returns the full token bundle.
  async customerLogin() {
    if (!this.custEmail || !this.custPassword) return;
    this.logging_in = true;
    this.loginReq = {
      url: `${this.apiBase}/auth/login`,
      method: 'POST',
      body: { email: this.custEmail, password: '••••••••' },  // masked for display
      withCredentials: false,
    };
    this.loginRes = null;
    this.custBundle = null;
    const t0 = performance.now();
    try {
      const res = await this.http.post(this.loginReq.url, {
        email: this.custEmail,
        password: this.custPassword,
      }, { observe: 'response' }).toPromise() as HttpResponse<any>;
      this.loginRes = {
        status: res.status,
        ok: res.ok,
        body: this._mask_bundle(res.body),
        durationMs: Math.round(performance.now() - t0),
      };
      if (res.body?.accessToken || res.body?.access_token) {
        // f2-admin-service /auth/login returns {accessToken, idToken,
        // refreshToken} (camelCase per the Cognito InitiateAuth wrapper).
        this.custBundle = {
          access_token: res.body.accessToken || res.body.access_token,
          id_token:     res.body.idToken     || res.body.id_token,
          refresh_token: res.body.refreshToken || res.body.refresh_token,
          user: res.body.user || {},
        };
      }
    } catch (e: any) {
      this.loginRes = {
        status: e?.status ?? null,
        ok: false,
        body: e?.error,
        error: e?.message || 'request failed',
        durationMs: Math.round(performance.now() - t0),
      };
    } finally {
      this.logging_in = false;
    }
  }

  clearCustomerSession() {
    this.custEmail = '';
    this.custPassword = '';
    this.loginReq = null;
    this.loginRes = null;
    this.custBundle = null;
  }

  // Decode an unverified JWT for display. NEVER trust these claims for
  // auth decisions — this is a viewer only.
  decodeJwt(jwt: string | undefined): any {
    if (!jwt) return null;
    const parts = jwt.split('.');
    if (parts.length < 2) return null;
    try {
      const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = b64 + '==='.slice((b64.length + 3) % 4);
      return JSON.parse(atob(padded));
    } catch { return null; }
  }

  // Show only the first 32 chars + length for long token strings so the
  // response panel stays readable. Caller can still copy the raw bundle
  // via the copy button.
  private _mask_bundle(body: any): any {
    if (!body || typeof body !== 'object') return body;
    const out: any = { ...body };
    for (const k of ['accessToken','idToken','refreshToken','access_token','id_token','refresh_token']) {
      if (typeof out[k] === 'string') {
        out[k] = `${out[k].slice(0, 32)}… (${out[k].length} chars)`;
      }
    }
    return out;
  }

  // Copy curl-equivalent for the most recent mint call. Emits a
  // single-line command so it pastes cleanly into either bash or
  // Windows cmd.exe (no `\` line continuations — cmd rejects those,
  // 2026-07-08 Mike report). Uses bodyRaw (the wire-shape untruncated
  // body) instead of the human-readable `body` field which shows
  // `token: "eyJhb… (237 chars)"` — that ellipsis + count is a display
  // artifact and pasted into curl produced an invalid JWT.
  //
  // --ssl-no-revoke bakes in a Windows Schannel workaround — corp
  // networks and even some home ISPs can't reach OCSP responders,
  // triggering CRYPT_E_NO_REVOCATION_CHECK on every call to real
  // TLS endpoints. Harmless on macOS / Linux where curl ignores it.
  copyCurl(req: MintRequest | null) {
    if (!req) return;
    const bodyForCurl = req.bodyRaw ?? req.body;
    // Escape internal `"` with `\"` and wrap the body in double quotes.
    // This is the ONE quoting style curl accepts on both Windows cmd
    // and POSIX bash — cmd doesn't treat `'` as a string delimiter at
    // all, so a single-quoted body sends the literal single-quote chars
    // to the server (backend gets `'{"token":"..."}'` and 400s on the
    // JSON parse). Escaped double quotes work verbatim in both shells.
    // 2026-07-08 Mike report: curl returned "InternalServerError" —
    // was actually 400 from server-side JSON parse failure.
    const bodyJson = JSON.stringify(bodyForCurl).replace(/"/g, `\\"`);
    const cookieFlag = req.withCredentials
      ? ` --cookie "f2_id=<your admin cookie>"`
      : '';
    const line = `curl --ssl-no-revoke -X ${req.method} "${req.url}" -H "Content-Type: application/json"${cookieFlag} --data "${bodyJson}"`;
    navigator.clipboard.writeText(line);
  }

  copyText(t: any) {
    if (t === null || t === undefined) return;
    navigator.clipboard.writeText(typeof t === 'string' ? t : JSON.stringify(t, null, 2));
  }

  // IT-F2-437 Mike 2026-10-07 cid:9c7764d4 + cid:95bf7d39: paste-into-
  // ticket debug bundle for a mint/redeem response. Captures everything
  // Mike's panel shows PLUS the context he'd otherwise have to type:
  //   - request method + url + body (same truncation shape mintReq.body
  //     already uses, so tokens stay abbreviated)
  //   - response status / duration / ok flag / body / error
  //   - tester state at the time of the call: which customer + scanner_host
  //     + authPath + usePortalCustomerHost + omitPortalSlug +
  //     portalScannerInputMode + portalDryRun
  //   - ISO timestamp
  // Old copyText(mintRes.body) grabbed only body — on an HTTP 0 fetch
  // failure that's HttpClient's ErrorEvent (`{isTrusted: true}`), not
  // the actual error string, so a paste was useless for diagnosis.
  copyResponseBundle(res: any, req?: any): void {
    if (!res) return;
    const lines: string[] = [];
    lines.push(`timestamp: ${new Date().toISOString()}`);
    if (req?.method && req?.url) lines.push(`${req.method} ${req.url}`);
    const statusLabel = `HTTP ${res.status ?? '(no status)'} · ${res.durationMs ?? '?'}ms${res.ok ? ' · ok' : ''}`;
    lines.push(statusLabel);

    // Tester state snapshot — the knobs that drive URL/body construction.
    // Mike cid 95bf7d39: "do we have enough detail" — these fields are
    // what separates a "which variant did I just click" question from a
    // self-identifying paste.
    const cust = this.selectedPortalCustomer;
    lines.push('');
    lines.push('tester state:');
    if (cust) {
      lines.push(`  customer: ${cust.slug}${cust.scanner_host ? ' (scanner_host: ' + cust.scanner_host + ')' : ''}`);
    } else {
      lines.push(`  customer: (none selected)`);
    }
    lines.push(`  authPath: ${this.authPath}`);
    lines.push(`  usePortalCustomerHost: ${this.usePortalCustomerHost}`);
    lines.push(`  omitPortalSlug: ${this.omitPortalSlug}`);
    lines.push(`  portalScannerInputMode: ${this.portalScannerInputMode}${this.portalScannerSlug ? ' (slug: ' + this.portalScannerSlug + ')' : ''}`);
    lines.push(`  portalDryRun: ${this.portalDryRun}`);

    if (req?.body !== undefined && req?.body !== null) {
      lines.push('');
      lines.push('request body:');
      try { lines.push(typeof req.body === 'string' ? req.body : JSON.stringify(req.body, null, 2)); }
      catch { lines.push(String(req.body)); }
    }

    if (res.body !== undefined && res.body !== null) {
      lines.push('');
      lines.push('response body:');
      try { lines.push(typeof res.body === 'string' ? res.body : JSON.stringify(res.body, null, 2)); }
      catch { lines.push(String(res.body)); }
    }
    if (res.error) {
      lines.push('');
      lines.push('error:');
      lines.push(String(res.error));
    }
    navigator.clipboard.writeText(lines.join('\n'));
  }

  // After a REAL grant's sid is USED (Open Tab / Embed Iframe), revert to
  // dry-run for the operator's next click. Belt-and-suspenders with the
  // toggle's confirm modal — the operator gave one-shot consent for THIS
  // grant; further grants need a fresh opt-in. Banner updates via the
  // existing [ngClass] bindings on portalDryRun. Only fires when portal
  // path + currently in real mode + the operator wasn't already in dry-run
  // (avoids no-op churn for dry-run flows).
  private _postGrantUseRevertToDryRun() {
    if (!this.isPortalPath) return;
    if (this.portalDryRun) return;
    this.portalDryRun = true;
    this._logEvent('mode', 'auto-reverted to dry-run after grant consumed (UI banner now yellow)');
  }

  // SECURITY: turning OFF dry-run flips the tester into write mode.
  // Confirmation modal is mandatory — once OFF, the next "Grant Permissions
  // (REAL)" click will mutate the target email's Cognito record. The
  // confirmation names the customer + email so the operator can't
  // accidentally grant against a stale form state.
  toggleDryRun(ev: Event) {
    const turnOn = !(ev.target as HTMLInputElement).checked;
    if (turnOn) {
      this.portalDryRun = true;
      this._logEvent('mode', 'dry-run ON (safe)');
      return;
    }
    // Going OFF requires explicit confirmation.
    const cust = this.selectedPortalCustomer?.slug || '(no customer)';
    const target = this.portalEmailSourceValue || '(no email entered)';
    const msg = `Turn OFF dry-run?\n\nNext "Grant Permissions" click will WRITE real entitlements to Cognito for:\n  • customer: ${cust}\n  • target email: ${target}\n\nThis is the production grant path. The user will gain access to ${cust} permanently.\n\nProceed?`;
    if (confirm(msg)) {
      this.portalDryRun = false;
      this._logEvent('mode', `dry-run OFF (REAL grant mode) — customer=${cust} target=${target}`);
    } else {
      // Force the checkbox back to checked.
      (ev.target as HTMLInputElement).checked = false;
      this.portalDryRun = true;
      this._logEvent('mode', 'real-mode confirm declined → stayed on dry-run');
    }
  }

  // Serialize the full debug bundle (event log + current form state +
  // last request/response panels) and copy to clipboard as pretty JSON.
  // The operator pastes this into chat so a developer gets the exact
  // repro without ambiguity.
  copyDebugBundle() {
    const bundle = {
      generated_at: new Date().toISOString(),
      url: typeof location !== 'undefined' ? location.href : null,
      auth_path: this.authPath,
      portal: {
        selectedPortalSlug: this.selectedPortalSlug,
        portalDryRun: this.portalDryRun,
        portalAllowCreateAccount: this.portalAllowCreateAccount,
        portalExpSeconds: this.portalExpSeconds,
        portalFirstName: this.portalFirstName,
        portalLastName: this.portalLastName,
        portalPhone: this.portalPhone,
        portalEmailSourceClaim: this.portalEmailSourceClaim,
        portalEmailSourceValue: this.portalEmailSourceValue,
        portalMemberships: this.portalMemberships,
        portalScannerAliases: this.portalScannerAliases,
        portalPermsJson: this.portalPermsJson,
        portalExtrasJson: this.portalExtrasJson,
        claimsEditorJson: this.claimsEditorJson,
        jwtPreviewJson: this.jwtPreviewJson,
      },
      admin_path: {
        selectedScannerId: this.selectedScannerId,
        redeemAlsoTest: this.redeemAlsoTest,
      },
      customer_path: {
        custEmail: this.custEmail,
        // password intentionally omitted from the bundle
        custBundlePresent: !!this.custBundle,
      },
      requests: {
        signTokenReq: this.signTokenReq,
        signTokenRes: this._redactBundle(this.signTokenRes),
        loginReq: this.loginReq,
        loginRes: this._redactBundle(this.loginRes),
        mintReq: this.mintReq,
        mintRes: this.mintRes,
        redeemReq: this.redeemReq,
        redeemRes: this.redeemRes,
        exchangeReq: this.exchangeReq,
        exchangeRes: this._redactBundle(this.exchangeRes),
      },
      iframe: {
        iframeSrc: this.iframeSrc,
        iframeKey: this.iframeKey,
      },
      events: this.debugEvents,
    };
    const json = JSON.stringify(bundle, null, 2);
    navigator.clipboard.writeText(json);
    this._logEvent('action', `copied debug bundle (${this.debugEvents.length} events, ${json.length} chars)`);
  }
  // Strip JWTs from sign-token response and access/id/refresh tokens from
  // login response — they're long and not useful for debug; their lengths
  // and decoded claims (if displayed) are sufficient.
  private _redactBundle(res: any): any {
    if (!res) return res;
    const out = JSON.parse(JSON.stringify(res));
    if (out?.body?.token && typeof out.body.token === 'string') {
      out.body.token = `<REDACTED ${out.body.token.length}-char JWT>`;
    }
    for (const k of ['access_token', 'id_token', 'refresh_token']) {
      if (out?.body?.[k] && typeof out.body[k] === 'string') {
        out.body[k] = `<REDACTED ${out.body[k].length}-char ${k}>`;
      }
    }
    return out;
  }

  prettyJson(v: any): string {
    try { return JSON.stringify(v, null, 2); } catch { return String(v); }
  }

  // Extracts the ?sid=... from a returned url (or null if none).
  private _extractSid(url: string | undefined): string | null {
    if (!url) return null;
    try {
      const u = new URL(url);
      return u.searchParams.get('sid');
    } catch { return null; }
  }

  // Surface the sid + parsed host for the admin to confirm the URL is on
  // the host they expect (catches misconfigured Scanner.Url quickly).
  // Display-only URL parts. `.sid` is the credential value (either the
  // 43-char SID or the full JWT in token_query mode) — the field name is
  // kept for back-compat with the template's existing bindings, but in
  // token_query mode it carries a JWT, not a SID. The template branches
  // on returnedHandoffMode to show the right label.
  parsedUrl(): { host: string; pathname: string; sid: string | null } | null {
    const url = this.mintRes?.body?.url;
    if (!url) return null;
    try {
      const u = new URL(url);
      const sid = u.searchParams.get('sid');
      const token = u.searchParams.get('token');
      return {
        host: u.host,
        pathname: u.pathname,
        sid: sid !== null ? sid : token,
      };
    } catch { return null; }
  }
}
